<template>
  <div class="profile-container">
    <!-- Profile Header Section -->
    <div class="profile-header-section">
      <div class="profile-header-card compact">
        <div class="profile-header-content compact">
          <div class="profile-basic-info compact">
            <div class="profile-avatar-section compact" @click="openAvatarModal" v-if="athlete && athlete.profile">
              <img :src="athlete.profile" :alt="(athlete.firstname || '') + ' ' + (athlete.lastname || '')" class="profile-avatar compact" />
              <div class="avatar-overlay">
                <span class="change-avatar-text">{{ $t('profile.changeAvatar') }}</span>
              </div>
            </div>
            <div class="profile-details compact">
              <div class="profile-meta compact">
                <div class="profile-info-item compact" v-if="athlete && athlete.firstname">
                  <font-awesome-icon icon="user" class="meta-icon compact" />
                  <span class="meta-label compact">{{ $t('profile.athlete_name') }}</span>
                  <span class="meta-value compact">
                    {{ athlete.firstname }} {{ athlete.lastname }}
                    <span v-if="isPro" class="pro-badge" :title="$t('profile.proMember')">
                      <font-awesome-icon icon="crown" class="pro-icon" />
                      PRO
                    </span>
                  </span>
                </div>
                <div class="profile-info-item compact" v-if="athlete && athlete._id">
                  <font-awesome-icon icon="id-badge" class="meta-icon compact" />
                  <span class="meta-label compact">{{ $t('profile.id') }}</span>
                  <span class="meta-value compact">{{ athlete._id }}</span>
                </div>
                <div class="profile-info-item compact" v-if="athlete && athlete.email">
                  <font-awesome-icon icon="envelope" class="meta-icon compact" />
                  <span class="meta-label compact">{{ $t('profile.email') }}</span>
                  <span class="meta-value compact">{{ athlete.email }}</span>
                </div>
                <div class="profile-info-item compact" v-if="athlete">
                  <font-awesome-icon icon="running" class="meta-icon compact" />
                  <span class="meta-label compact">{{ $t('profile.total_activities') }}</span>
                  <span class="meta-value compact">{{ totalActivities }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="profile-actions" v-if="isLoggedIn">
            <div class="action-buttons compact">
              <button @click="openProgressModal" class="btn-progress-tracker compact" title="View Progress Tracker">
                <font-awesome-icon icon="chart-line" class="btn-icon compact" />
              </button>
              <button @click="openEditModal" class="btn-edit-profile compact" title="Edit Profile">
                <font-awesome-icon icon="edit" class="btn-icon compact" />
              </button>
              <button @click="logout" class="btn-logout compact" title="Logout">
                <font-awesome-icon icon="sign-out-alt" class="btn-icon compact" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Subscription Pricing Section -->
    <SubscriptionPricing />

    <!-- Main Content Tabs -->
    <div class="main-content-section">
      <div class="content-tabs">
        <b-nav tabs class="nav-tabs-custom">
          <b-nav-item
            :active="activeTab === 'my-activities'"
            @click.prevent="switchToMyActivitiesTab"
            class="nav-item-custom"
          >
            <font-awesome-icon icon="tachometer-alt" class="nav-icon" />
            <span class="nav-text">{{ $t('profile.myActivities') }}</span>
          </b-nav-item>
          <b-nav-item
            :active="activeTab === 'challenges'"
            @click.prevent="switchToChallengesTab"
            class="nav-item-custom"
          >
            <font-awesome-icon icon="trophy" class="nav-icon" />
            <span class="nav-text">{{ $t('profile.myChallenges') }}</span>
          </b-nav-item>
          <b-nav-item
            :active="activeTab === 'ai-history'"
            @click.prevent="switchToAiHistoryTab"
            class="nav-item-custom"
          >
            <font-awesome-icon icon="robot" class="nav-icon" />
            <span class="nav-text">{{ $t('profile.myAiHistory') }}</span>
          </b-nav-item>
        </b-nav>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- My Activities Tab -->
        <div v-show="activeTab === 'my-activities'" :class="['tab-pane', { active: activeTab === 'my-activities' }]">
          <div class="activities-section">
            <div class="activities-header">
              <div class="section-icon">
                <font-awesome-icon icon="tachometer-alt" class="text-primary" />
              </div>
              <h2 class="section-title">{{ $t('profile.myActivities') }}</h2>
            </div>

            <!-- Loading State -->
            <div v-if="activitiesLoading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Loading...</span>
              </div>
              <p class="mt-2">{{ $t('profile.loading') }}</p>
            </div>

            <!-- Error State -->
            <div v-else-if="activitiesError" class="alert alert-danger" role="alert">
              <font-awesome-icon icon="exclamation-triangle" class="mr-2" />
              {{ activitiesError }}
            </div>

            <!-- Activities Grid -->
            <div v-else-if="activitiesData.length > 0" class="activities-grid">
              <div class="row g-4">
                <div class="col-12" v-for="(activity, index) in activitiesData" :key="`activity-${activity.id}-${index}`">
                  <div class="activity-card">
                    <div class="activity-card-header">
                      <div class="activity-type-badge">
                        <font-awesome-icon
                          :icon="getActivityIcon(activity.type)"
                          class="activity-icon"
                        />
                        <span class="activity-type-text">{{ activity.type }}</span>
                      </div>
                      <div class="activity-header-actions">
                        <button
                          class="btn-ai-analysis-action"
                          @click="showAiTracklogStatsModal(activity)"
                        >
                          <font-awesome-icon icon="brain" class="btn-icon-ai" />
                          <span class="btn-text-ai">AI Analysis</span>
                        </button>
                        <div class="activity-date">
                          {{ formatActivityDate(activity.start_date_local) }}
                        </div>
                      </div>
                    </div>

                    <div class="activity-card-body">
                      <h5 class="activity-name">{{ activity.name }}</h5>
                      <!-- Activity Metrics -->
                      <div class="activity-metrics">
                        <div class="metric-row">
                          <div class="metric-item">
                            <font-awesome-icon icon="route" class="metric-icon" />
                            <span class="metric-label">{{ $t('profile.distance') }}</span>
                            <span class="metric-value">{{ activity.distance_km }} km</span>
                          </div>
                          <div class="metric-item">
                            <font-awesome-icon icon="stopwatch" class="metric-icon" />
                            <span class="metric-label">{{ $t('profile.pace') }}</span>
                            <span class="metric-value">{{ activity.pace }} min/km</span>
                          </div>
                        </div>

                        <div class="metric-row">
                          <div class="metric-item">
                            <font-awesome-icon icon="clock" class="metric-icon" />
                            <span class="metric-label">Moving Time</span>
                            <span class="metric-value">{{ formatTime(activity.moving_time) }}</span>
                          </div>
                          <div class="metric-item">
                            <font-awesome-icon icon="hourglass-half" class="metric-icon" />
                            <span class="metric-label">Elapsed Time</span>
                            <span class="metric-value">{{ formatTime(activity.elapsed_time || activity.moving_time) }}</span>
                          </div>
                        </div>

                        <div class="metric-row">
                          <div class="metric-item" v-if="activity.type === 'Ride' || activity.type === 'VirtualRide'">
                            <font-awesome-icon icon="tachometer-alt" class="metric-icon" />
                            <span class="metric-label">{{ $t('profile.avgSpeed') }}</span>
                            <span class="metric-value">{{ activity.average_speed_kmh }} km/h</span>
                          </div>
                          <div class="metric-item" v-else-if="activity.average_heartrate">
                            <font-awesome-icon icon="heartbeat" class="metric-icon" />
                            <span class="metric-label">{{ $t('profile.avgHR') }}</span>
                            <span class="metric-value">{{ activity.average_heartrate }} bpm</span>
                          </div>
                          <div class="metric-item" v-if="activity.total_elevation_gain > 0">
                            <font-awesome-icon icon="mountain" class="metric-icon" />
                            <span class="metric-label">{{ $t('profile.elevation') }}</span>
                            <span class="metric-value">{{ activity.total_elevation_gain }}m</span>
                          </div>
                        </div>
                      </div>

                      <!-- Map Container -->
                      <div v-if="activity.summary_polyline" class="activity-map-container">
                        <div
                          :id="'map-' + activity.id + '-' + index"
                          class="activity-map"
                          :data-polyline="activity.summary_polyline"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Load More Button -->
              <div v-if="activitiesHasMore" class="text-center mt-4">
                <button
                  @click="loadMoreActivities"
                  class="btn btn-outline-primary"
                  :disabled="activitiesLoadingMore"
                >
                  <span v-if="activitiesLoadingMore" class="spinner-border spinner-border-sm mr-2"></span>
                  {{ $t('profile.loadMore') }}
                </button>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="empty-state text-center py-5">
              <!-- Sync in Progress Message -->
              <div v-if="backgroundSyncInProgress" class="alert alert-info mx-auto" style="max-width: 600px;">
                <div class="d-flex align-items-center justify-content-center mb-2">
                  <span class="spinner-border spinner-border-sm me-2"></span>
                  <strong>{{ $t('profile.syncInProgressTitle') }}</strong>
                </div>
                <p class="mb-0">{{ $t('profile.tooFewActivitiesModalSyncStarted') }}</p>
              </div>

              <!-- Default Empty State -->
              <div v-else>
                <font-awesome-icon icon="running" class="empty-icon" />
                <h4 class="empty-title">{{ $t('profile.noActivities') }}</h4>
                <p class="empty-description">{{ $t('profile.noActivitiesDescription') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Challenges Tab -->
        <div v-show="activeTab === 'challenges'" :class="['tab-pane', { active: activeTab === 'challenges' }]">
          <div class="challenges-section">
            <div class="challenges-header">
              <div class="section-icon">
                <font-awesome-icon icon="trophy" class="text-warning" />
              </div>
              <h2 class="section-title">{{ $t('profile.myChallenges') }}</h2>
            </div>

            <!-- Desktop View -->
            <div class="d-none d-lg-block">
              <div class="row g-4">
                <div class="col-lg-6 col-md-6" v-for="(event, idx) in events" :key="idx">
                  <div class="challenge-card">
                    <div class="challenge-card-header">
                      <div class="challenge-status-section">
                        <span v-if="isUpcomingEvent(event.from*1000)" class="status-badge upcoming">
                          <font-awesome-icon icon="clock" class="status-icon" />
                          <span class="status-text">{{ $t('profile.upcoming') }}</span>
                        </span>
                  <span v-if="isExpiredEvent(event.to*1000)" class="status-badge expired">
                    <font-awesome-icon icon="calendar-check" class="status-icon" />
                    <span class="status-text">{{ $t('profile.ended') }}</span>
                  </span>
                  <span v-if="!isUpcomingEvent(event.from*1000) && !isExpiredEvent(event.to*1000)" class="status-badge active">
                    <font-awesome-icon icon="play-circle" class="status-icon" />
                    <span class="status-text">{{ $t('profile.active') }}</span>
                  </span>
                </div>
                <div class="challenge-type-section">
                  <font-awesome-icon icon="swimmer" v-if="event.type==='Swim'" class="type-icon swim" />
                  <font-awesome-icon icon="biking" v-if="event.type==='Ride'" class="type-icon bike" />
                  <font-awesome-icon icon="running" v-if="event.type==='Run'" class="type-icon run" />
                  <font-awesome-icon icon="walking" v-if="event.type==='Walk'" class="type-icon walk" />
                  <span v-for="(type, typeIdx) in event.type" :key="typeIdx" class="type-icon-wrapper">
                    <font-awesome-icon icon="swimmer" v-if="type=='Swim'" class="type-icon swim" />
                    <font-awesome-icon icon="biking" v-if="type=='Ride'" class="type-icon bike" />
                    <font-awesome-icon icon="running" v-if="type=='Run'" class="type-icon run" />
                    <font-awesome-icon icon="walking" v-if="type=='Walk'" class="type-icon walk" />
                  </span>
                </div>
              </div>

              <div class="challenge-card-body">
                <h3 class="challenge-name">{{ event.name }}</h3>

                <!-- Challenge Timeline: Start and End Centered in One Row -->
                <div class="challenge-timeline single-row centered">
                  <div class="timeline-item">
                    <div class="timeline-icon">
                      <font-awesome-icon icon="calendar-alt" />
                    </div>
                    <div class="timeline-content">
                      <div class="timeline-label">{{ $t('profile.start') }}</div>
                      <div class="timeline-value">{{ formatEventDate(event.from * 1000) }}</div>
                    </div>
                  </div>
                  <div class="timeline-separator">—</div>
                  <div class="timeline-item">
                    <div class="timeline-icon">
                      <font-awesome-icon icon="calendar-check" />
                    </div>
                    <div class="timeline-content">
                      <div class="timeline-label">{{ $t('profile.end') }}</div>
                      <div class="timeline-value">{{ formatEventDate(event.to * 1000) }}</div>
                    </div>
                  </div>
                </div>

                <div class="challenge-actions">
                  <router-link :to="{name: 'eventDetail', params:{slug: event.slug}}" class="action-button secondary">
                    <span class="button-text">{{ $t('profile.viewDetails') }}</span>
                    <font-awesome-icon icon="external-link-alt" class="button-icon" />
                  </router-link>
                  <router-link :to="{name: 'eventDashboard', params:{slug: event.slug}}" class="action-button primary">
                    <span class="button-text">{{ $t('profile.dashboard') }}</span>
                    <font-awesome-icon icon="chart-line" class="button-icon" />
                  </router-link>
                  <button class="action-button achievement" @click="showAchievement(event)">
                    <span class="button-text">{{ $t('profile.myAchievement') }}</span>
                    <font-awesome-icon icon="medal" class="button-icon" />
                  </button>
                  <button class="action-button ai-gen" @click="handleAiGen(event)">
                    <span class="button-text">{{ $t('profile.aiGen') }}</span>
                    <font-awesome-icon icon="robot" class="button-icon" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile View -->
      <div class="d-lg-none">
        <div class="row g-3">
          <div class="col-12" v-for="(event, idx) in events" :key="idx">
            <div class="challenge-card-mobile">
              <div class="challenge-mobile-header">
                <div class="challenge-mobile-status">
                  <span v-if="isUpcomingEvent(event.from*1000)" class="mobile-status-badge upcoming">
                    <font-awesome-icon icon="clock" class="mobile-status-icon" />
                  </span>
                  <span v-if="isExpiredEvent(event.to*1000)" class="mobile-status-badge expired">
                    <font-awesome-icon icon="calendar-check" class="mobile-status-icon" />
                  </span>
                  <span v-if="!isUpcomingEvent(event.from*1000) && !isExpiredEvent(event.to*1000)" class="mobile-status-badge active">
                    <font-awesome-icon icon="play-circle" class="mobile-status-icon" />
                  </span>
                </div>
                <div class="challenge-mobile-type">
                  <font-awesome-icon icon="swimmer" v-if="event.type==='Swim'" class="mobile-type-icon swim" />
                  <font-awesome-icon icon="biking" v-if="event.type==='Ride'" class="mobile-type-icon bike" />
                  <font-awesome-icon icon="running" v-if="event.type==='Run'" class="mobile-type-icon run" />
                  <font-awesome-icon icon="walking" v-if="event.type==='Walk'" class="mobile-type-icon walk" />
                  <span v-for="(type, typeIdx) in event.type" :key="typeIdx" class="mobile-type-icon-wrapper">
                    <font-awesome-icon icon="swimmer" v-if="type=='Swim'" class="mobile-type-icon swim" />
                    <font-awesome-icon icon="biking" v-if="type=='Ride'" class="mobile-type-icon bike" />
                    <font-awesome-icon icon="running" v-if="type=='Run'" class="mobile-type-icon run" />
                    <font-awesome-icon icon="walking" v-if="type=='Walk'" class="mobile-type-icon walk" />
                  </span>
                </div>
              </div>

              <div class="challenge-mobile-content">
                <div class="challenge-mobile-header">
                  <h4 class="challenge-mobile-name">{{ event.name }}</h4>
                  <font-awesome-icon icon="chevron-right" class="mobile-arrow-icon" />
                </div>

                <!-- Challenge Timeline: Start and End Centered in One Row (Mobile) -->
                <div class="challenge-mobile-dates single-row centered">
                  <div class="mobile-date-item">
                    <font-awesome-icon icon="calendar-alt" class="mobile-date-icon" />
                    <span class="mobile-date-text">{{ formatEventDate(event.from * 1000) }}</span>
                  </div>
                  <span class="timeline-separator">—</span>
                  <div class="mobile-date-item">
                    <font-awesome-icon icon="calendar-check" class="mobile-date-icon" />
                    <span class="mobile-date-text">{{ formatEventDate(event.to * 1000) }}</span>
                  </div>
                </div>

                <div class="challenge-mobile-actions single-row centered">
                  <router-link :to="{name: 'eventDetail', params:{slug: event.slug}}" class="mobile-action-btn secondary">
                    <font-awesome-icon icon="external-link-alt" />
                  </router-link>
                  <router-link :to="{name: 'eventDashboard', params:{slug: event.slug}}" class="mobile-action-btn primary">
                    <font-awesome-icon icon="chart-line" />
                  </router-link>
                  <button class="mobile-action-btn achievement" @click="showAchievement(event)">
                    <font-awesome-icon icon="medal" />
                  </button>
                  <button class="mobile-action-btn ai-gen" @click="handleAiGen(event)">
                    <font-awesome-icon icon="robot" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

            <!-- Infinite Scroll Loading Indicator -->
            <div v-if="loadingMore && events.length > 0" class="infinite-scroll-loading">
              <div class="loading-indicator">
                <font-awesome-icon icon="running" class="fa-3x running-animate" />
                <span class="loading-text">{{ $t('profile.loadingMoreChallenges') }}</span>
              </div>
            </div>

            <!-- Empty State -->
            <div v-if="events.length === 0" class="text-center py-5">
              <div class="empty-state">
                <div class="empty-icon-wrapper">
                  <font-awesome-icon icon="trophy" class="empty-icon" />
                </div>
                <h3 class="empty-title">{{ $t('profile.noChallenges') }}</h3>
                <p class="empty-description">{{ $t('profile.joinChallenges') }}</p>
              </div>
            </div>

            <!-- Load More Button (fallback for infinite scroll) -->
            <!-- REMOVED: No more load more button, only infinite scroll indicator -->
          </div>
        </div>

        <!-- AI History Tab -->
        <div v-show="activeTab === 'ai-history'" :class="['tab-pane', { active: activeTab === 'ai-history' }]">
          <div class="ai-history-section">
            <div class="ai-history-header">
              <div class="section-icon">
                <font-awesome-icon icon="robot" class="text-primary" />
              </div>
              <h2 class="section-title">{{ $t('profile.myAiHistory') }}</h2>
              <div class="header-actions">
                <button
                  @click="refreshAiHistory"
                  class="btn btn-outline-primary btn-sm refresh-btn"
                  :disabled="aiHistoryLoading"
                  :title="$t('profile.refreshAiHistory')"
                >
                  <font-awesome-icon
                    :icon="aiHistoryLoading ? 'spinner' : 'sync-alt'"
                    :spin="aiHistoryLoading"
                    class="me-1"
                  />
                  {{ $t('profile.refresh') }}
                </button>
                <div class="polling-controls" v-if="aiHistoryData && aiHistoryData.data.length > 0">
                  <button
                    @click="togglePolling"
                    class="btn btn-sm"
                    :class="isPolling ? 'btn-outline-danger' : 'btn-outline-success'"
                    :title="isPolling ? $t('profile.stopPolling') : $t('profile.startPolling')"
                  >
                    <font-awesome-icon
                      :icon="isPolling ? 'pause' : 'play'"
                      class="me-1"
                    />
                    {{ isPolling ? $t('profile.stopPolling') : $t('profile.startPolling') }}
                  </button>
                  <span v-if="isPolling" class="polling-status text-muted">
                    <font-awesome-icon icon="clock" class="me-1" />
                    {{ $t('profile.autoRefreshing') }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="aiHistoryLoading" class="text-center py-4">
              <font-awesome-icon icon="running" class="fa-3x running-animate" />
              <span class="ms-2">{{ $t('profile.loadingAiHistory') }}</span>
            </div>

            <!-- Error State -->
            <div v-else-if="aiHistoryError" class="alert alert-danger">
              {{ aiHistoryError }}
            </div>

            <!-- AI History Content -->
            <div v-else-if="aiHistoryData && aiHistoryData.data.length > 0">
              <!-- Desktop Table View -->
              <div class="d-none d-md-block">
                <div class="ai-history-table-wrapper">
                  <table class="modern-compact-table">
                    <thead>
                      <tr>
                        <th>{{ $t('profile.contentType') }}</th>
                        <th>{{ $t('profile.statusTitle') }}</th>
                        <th>{{ $t('profile.contentTitle') }}</th>
                        <th>{{ $t('profile.privacy') }}</th>
                        <th>{{ $t('profile.updatedAt') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(record, index) in aiHistoryData.data" :key="record.activity_id + '-' + record.content_type + '-' + record.created_at + '-' + index">
                        <td>
                          <span class="content-type-badge" :class="record.content_type">
                            <font-awesome-icon
                              :icon="record.content_type === 'image' ? 'image' : record.content_type === 'video' ? 'video' : 'chart-line'"
                              class="type-icon"
                            />
                          </span>
                        </td>
                        <td>
                          <span class="status-badge" :class="record.status">
                            {{ $t('profile.aiStatus.' + record.status) }}
                          </span>
                        </td>
                        <td class="content-url content-text">
                          <div v-if="record.content_text" class="content-text-container">
                            <div class="content-preview">
                              <div class="preview-header">
                                <h6 class="activity-title" v-if="record.activity_name">{{ record.activity_name }}</h6>
                                <button
                                  class="btn btn-sm btn-outline-primary preview-btn"
                                  @click="showContentText(record.content_text, record.content_type, record.activity_id)"
                                >
                                  <font-awesome-icon icon="eye" class="me-1" />
                                  {{ $t('profile.viewAnalysis') }}
                                </button>
                              </div>
                            </div>
                          </div>
                          <div v-if="record.content_url" class="content-actions">
                            <button
                              class="btn btn-sm btn-outline-primary me-2"
                              @click="previewContent(record.content_url, record.content_type)"
                              :title="$t('profile.previewContent')"
                            >
                              <font-awesome-icon icon="eye" />
                              {{ $t('profile.preview') }}
                            </button>
                            <a
                              :href="record.content_url"
                              target="_blank"
                              class="btn btn-sm btn-outline-secondary"
                              :title="$t('profile.openInNewTab')"
                            >
                              <font-awesome-icon icon="external-link-alt" />
                              {{ $t('profile.open') }}
                            </a>
                          </div>
                        </td>
                        <td class="privacy-column">
                          <div v-if="record.status === 'finished'" class="privacy-toggle">
                            <button
                              class="btn btn-sm"
                              :class="record.is_private ? 'btn-outline-danger' : 'btn-outline-success'"
                              @click="togglePrivacy(record)"
                              :disabled="privacyLoading[record.activity_id + '-' + record.content_type]"
                              :title="record.is_private ? $t('profile.makePublic') : $t('profile.makePrivate')"
                            >
                              <font-awesome-icon
                                :icon="record.is_private ? 'lock' : 'unlock'"
                                class="me-1"
                              />
                              <span v-if="!privacyLoading[record.activity_id + '-' + record.content_type]">
                                {{ record.is_private ? $t('profile.private') : $t('profile.public') }}
                              </span>
                              <span v-else>
                                <font-awesome-icon icon="spinner" spin />
                              </span>
                            </button>
                          </div>
                          <span v-else class="text-muted">{{ $t('profile.notAvailable') }}</span>
                        </td>
                        <td class="created-at">{{ formatTimestamp(record.updated_at) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Mobile Card View -->
              <div class="d-md-none">
                <div class="ai-history-cards">
                  <div
                    v-for="(record, index) in aiHistoryData.data"
                    :key="record.activity_id + '-' + record.content_type + '-' + record.created_at + '-' + index"
                    class="ai-history-card"
                  >
                    <div class="card-header">
                      <div class="activity-info">
                        <span class="content-type-badge" :class="record.content_type">
                          <font-awesome-icon
                            :icon="record.content_type === 'image' ? 'image' : record.content_type === 'video' ? 'video' : 'chart-line'"
                            class="type-icon"
                          />
                        </span>
                        <span class="status-badge" :class="record.status">
                          {{ $t('profile.aiStatus.' + record.status) }}
                        </span>
                      </div>
                    </div>
                    <div class="card-body">
                      <div class="content-info">
                        <div class="content-url content-text">
                          <div v-if="record.content_text" class="content-text-container">
                            <div class="content-preview">
                              <div class="preview-header">
                                <h6 class="activity-title" v-if="record.activity_name">{{ record.activity_name }}</h6>
                                <button
                                  class="btn btn-sm btn-outline-primary preview-btn"
                                  @click="showContentText(record.content_text, record.content_type, record.activity_id)"
                                >
                                  <font-awesome-icon icon="eye" class="me-1" />
                                  {{ $t('profile.viewAnalysis') }}
                                </button>
                              </div>
                            </div>
                          </div>
                          <div v-if="record.content_url" class="content-actions">
                            <button
                              class="btn btn-sm btn-outline-primary me-2"
                              @click="previewContent(record.content_url, record.content_type)"
                              :title="$t('profile.previewContent')"
                            >
                              <font-awesome-icon icon="eye" />
                              {{ $t('profile.preview') }}
                            </button>
                            <a
                              :href="record.content_url"
                              target="_blank"
                              class="btn btn-sm btn-outline-secondary"
                              :title="$t('profile.openInNewTab')"
                            >
                              <font-awesome-icon icon="external-link-alt" />
                              {{ $t('profile.open') }}
                            </a>
                          </div>
                        </div>
                        <div class="privacy-controls">
                          <div v-if="record.status === 'finished'" class="privacy-toggle">
                            <button
                              class="btn btn-sm"
                              :class="record.is_private ? 'btn-outline-danger' : 'btn-outline-success'"
                              @click="togglePrivacy(record)"
                              :disabled="privacyLoading[record.activity_id + '-' + record.content_type]"
                              :title="record.is_private ? $t('profile.makePublic') : $t('profile.makePrivate')"
                            >
                              <font-awesome-icon
                                :icon="record.is_private ? 'lock' : 'unlock'"
                                class="me-1"
                              />
                              <span v-if="!privacyLoading[record.activity_id + '-' + record.content_type]">
                                {{ record.is_private ? $t('profile.private') : $t('profile.public') }}
                              </span>
                              <span v-else>
                                <font-awesome-icon icon="spinner" spin />
                              </span>
                            </button>
                          </div>
                          <span v-else class="text-muted">{{ $t('profile.notAvailable') }}</span>
                        </div>
                        <div class="created-at">
                          <font-awesome-icon icon="clock" class="time-icon" />
                          {{ formatTimestamp(record.updated_at) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Pagination -->
              <div v-if="aiHistoryData.pagination && aiHistoryData.pagination.total_pages > 1" class="pagination-wrapper">
                <nav aria-label="AI History Pagination">
                  <ul class="pagination justify-content-center">
                    <li class="page-item" :class="{ disabled: !aiHistoryData.pagination.has_prev }">
                      <button
                        class="page-link"
                        @click="loadAiHistory(aiHistoryData.pagination.current_page - 1)"
                        :disabled="!aiHistoryData.pagination.has_prev"
                      >
                        {{ $t('profile.previous') }}
                      </button>
                    </li>
                    <li class="page-item active">
                      <span class="page-link">
                        {{ aiHistoryData.pagination.current_page }} / {{ aiHistoryData.pagination.total_pages }}
                      </span>
                    </li>
                    <li class="page-item" :class="{ disabled: !aiHistoryData.pagination.has_next }">
                      <button
                        class="page-link"
                        @click="loadAiHistory(aiHistoryData.pagination.current_page + 1)"
                        :disabled="!aiHistoryData.pagination.has_next"
                      >
                        {{ $t('profile.next') }}
                      </button>
                    </li>
                  </ul>
                </nav>
                <div class="pagination-info text-center text-muted">
                  {{ $t('profile.showingResults', {
                    start: (aiHistoryData.pagination.offset + 1),
                    end: Math.min(aiHistoryData.pagination.offset + aiHistoryData.pagination.limit, aiHistoryData.pagination.total_count),
                    total: aiHistoryData.pagination.total_count
                  }) }}
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="aiHistoryData && aiHistoryData.data.length === 0" class="text-center py-5">
              <div class="empty-state">
                <div class="empty-icon-wrapper">
                  <font-awesome-icon icon="robot" class="empty-icon" />
                </div>
                <h3 class="empty-title">{{ $t('profile.noAiHistory') }}</h3>
                <p class="empty-description">{{ $t('profile.generateAiContent') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Profile Modal -->
    <b-modal
      id="edit-profile-modal"
      ref="modal"
      :title="$t('profile.editProfile')"
      @show="resetModal"
      @hidden="resetModal"
      @ok="handleOk"
      size="lg"
      centered
      :ok-disabled="profileUpdateLoading"
    >
      <template #modal-ok>
        <span v-if="profileUpdateLoading" class="spinner-border spinner-border-sm me-2"></span>
        {{ profileUpdateLoading ? $t('profile.saving') : $t('profile.save') }}
      </template>
      <div class="modal-content">
        <!-- Error Alert -->
        <div v-if="profileUpdateError" class="alert alert-danger alert-dismissible fade show" role="alert">
          <div class="d-flex align-items-start">
            <font-awesome-icon icon="exclamation-triangle" class="me-2 mt-1" />
            <div class="flex-grow-1">{{ profileUpdateError }}</div>
            <button type="button" class="btn-close" @click="profileUpdateError = null" aria-label="Close"></button>
          </div>
        </div>

        <!-- Success Alert -->
        <div v-if="profileUpdateSuccess" class="alert alert-success alert-dismissible fade show" role="alert">
          <div class="d-flex align-items-start">
            <font-awesome-icon icon="check-circle" class="me-2 mt-1" />
            <div class="flex-grow-1">{{ profileUpdateSuccess }}</div>
            <button type="button" class="btn-close" @click="profileUpdateSuccess = null" aria-label="Close"></button>
          </div>
        </div>

        <form ref="form" @submit.stop.prevent="handleSubmit">
          <div class="form-group">
            <label for="email-input" class="form-label">
              <font-awesome-icon icon="envelope" class="form-icon" />
              {{ $t('profile.emailAddress') }}
              <span class="optional-label">({{ $t('profile.optional') }})</span>
            </label>
            <input
              type="email"
              id="email-input"
              v-model="email"
              :class="['form-control', { 'is-invalid': emailState === false }]"
              :placeholder="$t('profile.emailPlaceholder')"
            />
            <div v-if="emailState === false" class="invalid-feedback">
              {{ $t('profile.invalidEmail') }}
            </div>
          </div>

          <!-- Age Field -->
          <div class="form-group">
            <label for="age-input" class="form-label">
              <font-awesome-icon icon="calendar" class="form-icon" />
              {{ $t('profile.age') }}
              <span class="optional-label">({{ $t('profile.optional') }})</span>
            </label>
            <input
              type="number"
              id="age-input"
              v-model="age"
              :class="['form-control', { 'is-invalid': ageState === false }]"
              :placeholder="$t('profile.agePlaceholder')"
              min="1"
              max="120"
            />
            <div v-if="ageState === false" class="invalid-feedback">
              {{ $t('profile.invalidAge') }}
            </div>
          </div>

          <!-- Weight Field -->
          <div class="form-group">
            <label for="weight-input" class="form-label">
              <font-awesome-icon icon="weight-hanging" class="form-icon" />
              {{ $t('profile.weight') }}
              <span class="optional-label">({{ $t('profile.optional') }})</span>
            </label>
            <div class="input-group">
              <input
                type="number"
                id="weight-input"
                v-model="weight"
                :class="['form-control', { 'is-invalid': weightState === false }]"
                :placeholder="$t('profile.weightPlaceholder')"
                min="1"
                max="500"
                step="0.1"
              />
              <span class="input-group-text">kg</span>
            </div>
            <div v-if="weightState === false" class="invalid-feedback">
              {{ $t('profile.invalidWeight') }}
            </div>
          </div>

          <!-- Height Field -->
          <div class="form-group">
            <label for="height-input" class="form-label">
              <font-awesome-icon icon="ruler" class="form-icon" />
              {{ $t('profile.height') }}
              <span class="optional-label">({{ $t('profile.optional') }})</span>
            </label>
            <div class="input-group">
              <input
                type="number"
                id="height-input"
                v-model="height"
                :class="['form-control', { 'is-invalid': heightState === false }]"
                :placeholder="$t('profile.heightPlaceholder')"
                min="50"
                max="300"
                step="1"
              />
              <span class="input-group-text">cm</span>
            </div>
            <small class="form-text text-muted">
              {{ $t('profile.heightHelp') }}
            </small>
            <div v-if="heightState === false" class="invalid-feedback">
              {{ $t('profile.invalidHeight') }}
            </div>
          </div>

          <!-- Gender Field -->
          <div class="form-group">
            <label for="gender-select" class="form-label">
              <font-awesome-icon icon="user" class="form-icon" />
              {{ $t('profile.gender') }}
              <span class="optional-label">({{ $t('profile.optional') }})</span>
            </label>
            <select
              id="gender-select"
              v-model="sex"
              :class="['form-control', 'form-select', { 'is-invalid': sexState === false }]"
            >
              <option value="">{{ $t('profile.genderUnknown') }}</option>
              <option value="male">{{ $t('profile.genderMale') }}</option>
              <option value="female">{{ $t('profile.genderFemale') }}</option>
            </select>
            <div v-if="sexState === false" class="invalid-feedback">
              {{ $t('profile.invalidGender') }}
            </div>
          </div>

          <!-- Auto Share to Strava Field -->
          <div class="form-group">
            <div class="form-check form-switch">
              <input
                type="checkbox"
                id="auto-share-strava"
                v-model="autoAnalyseAndShare"
                class="form-check-input"
              />
              <label for="auto-share-strava" class="form-check-label">
                <font-awesome-icon icon="share" class="form-icon me-2" />
                {{ $t('profile.autoAnalyseAndShare') }}
              </label>
            </div>
            <small class="form-text text-muted">
              {{ $t('profile.autoAnalyseAndShareHelp') }}
            </small>
          </div>

          <!-- Training Target Section -->
          <div class="form-group mt-4">
            <h6 class="form-section-title">
              <font-awesome-icon icon="bullseye" class="form-icon me-2" />
              {{ $t('profile.trainingTarget') }}
            </h6>
            <small class="form-text text-muted d-block mb-3">
              {{ $t('profile.trainingTargetHelp') }}
            </small>

            <!-- Target Finish Time -->
            <div class="form-group">
              <label for="target-finish-time">
                {{ $t('profile.targetFinishTime') }}
                <span class="optional-label">({{ $t('profile.optional') }})</span>
              </label>
              <div class="d-flex gap-2">
                <div class="flex-fill">
                  <input
                    type="number"
                    id="target-hours"
                    v-model="targetFinishTimeHours"
                    class="form-control"
                    :placeholder="$t('profile.hoursPlaceholder')"
                    min="0"
                    max="48"
                  />
                  <small class="form-text text-muted">{{ $t('profile.hours') }}</small>
                </div>
                <div class="flex-fill">
                  <input
                    type="number"
                    id="target-minutes"
                    v-model="targetFinishTimeMinutes"
                    class="form-control"
                    :placeholder="$t('profile.minutesPlaceholder')"
                    min="0"
                    max="59"
                  />
                  <small class="form-text text-muted">{{ $t('profile.minutes') }}</small>
                </div>
              </div>
              <small class="form-text text-muted mt-2">
                {{ $t('profile.targetFinishTimeHelp') }}
              </small>
            </div>

            <!-- Target Race Date -->
            <div class="form-group mt-3">
              <label for="target-race-date">
                {{ $t('profile.targetRaceDate') }}
                <span class="optional-label">({{ $t('profile.optional') }})</span>
              </label>
              <input
                type="date"
                id="target-race-date"
                v-model="targetRaceDate"
                class="form-control"
                :min="minRaceDate"
              />
              <small class="form-text text-muted">
                {{ $t('profile.targetRaceDateHelp') }}
              </small>
            </div>

            <!-- Target Sport Types -->
            <div class="form-group mt-3">
              <label for="target-sport-types">
                {{ $t('profile.targetSportTypes') }}
                <span class="optional-label">({{ $t('profile.optional') }})</span>
              </label>
              <select
                id="target-sport-types"
                v-model="targetSportTypes"
                class="form-control form-select"
                multiple
                size="4"
              >
                <option value="running">
                  🏃 {{ $t('profile.sportRunning') }}
                </option>
                <option value="cycling">
                  🚴 {{ $t('profile.sportCycling') }}
                </option>
                <option value="swimming">
                  🏊 {{ $t('profile.sportSwimming') }}
                </option>
                <option value="triathlon">
                  🏊🚴🏃 {{ $t('profile.sportTriathlon') }}
                </option>
              </select>
              <small class="form-text text-muted">
                {{ $t('profile.targetSportTypesHelp') }}
              </small>
            </div>

            <div class="form-group mt-3">
              <label for="goal-description">
                {{ $t('profile.goalDescription') }}
                <span class="optional-label">({{ $t('profile.optional') }})</span>
              </label>
              <textarea
                id="goal-description"
                v-model="goalDescription"
                class="form-control"
                rows="4"
                :placeholder="$t('profile.goalDescriptionPlaceholder')"
              ></textarea>
              <small class="form-text text-muted">
                {{ $t('profile.goalDescriptionHelp') }}
              </small>
            </div>
          </div>
        </form>
      </div>
    </b-modal>

    <!-- Achievement Modal -->
    <b-modal
      id="achievement-modal"
      v-model="achievementModalVisible"
      :title="$t('profile.myAchievement')"
      size="xl"
      centered
      scrollable
      body-class="achievement-modal-body"
    >
      <div v-if="achievementLoading" class="text-center py-4">
        <font-awesome-icon icon="running" class="fa-3x running-animate" />
        <span class="ms-2">{{ $t('profile.loadingAchievement') }}</span>
      </div>
      <div v-else-if="achievementError" class="alert alert-danger">
        {{ achievementError }}
      </div>
      <div v-else-if="achievementData && selectedEvent">
        <div class="achievement-summary">
          <div class="achievement-header">
            <div class="achievement-title">
              <h5 class="mb-0">{{ selectedEvent.name }}</h5>
              <small class="text-muted">{{ $t('profile.activityList') }}</small>
            </div>
            <div class="achievement-controls">
              <button @click="stats(achievementData.activities)" class="btn-stats">
                <font-awesome-icon icon="chart-bar" class="btn-icon" />
                <span v-if="!showStatsSummary">{{ $t('profile.showStats') }}</span>
                <span v-else>{{ $t('profile.hideStats') }}</span>
              </button>
            </div>
          </div>

          <!-- Stats Summary with Chart -->
          <div v-if="showStatsSummary" class="stats-summary">
            <!-- Chart Type Selector -->
            <div class="chart-type-selector">
              <button
                @click="chartType = 'bar'"
                :class="['chart-type-btn', { active: chartType === 'bar' }]"
                title="Bar Chart"
              >
                <font-awesome-icon icon="chart-bar" />
              </button>
              <button
                @click="chartType = 'doughnut'"
                :class="['chart-type-btn', { active: chartType === 'doughnut' }]"
                title="Doughnut Chart"
              >
                <font-awesome-icon icon="chart-pie" />
              </button>
            </div>

            <!-- Chart Section -->
            <div class="chart-section">
              <achievement-chart
                :stats="athleteStats"
                :chart-type="chartType"
              />
            </div>

            <!-- Stats Cards -->
            <div class="stats-grid">
              <div v-for="(stat, type) in athleteStats" :key="type" class="stat-card">
                <div class="stat-header">
                  <font-awesome-icon icon="swimmer" v-if="type=='Swim'" class="stat-icon swim"/>
                  <font-awesome-icon icon="biking" v-if="type=='Ride'" class="stat-icon bike"/>
                  <font-awesome-icon icon="running" v-if="type=='Run'" class="stat-icon run"/>
                  <font-awesome-icon icon="walking" v-if="type=='Walk'" class="stat-icon walk"/>
                  <span class="stat-type">{{ type }}</span>
                </div>
                <div class="stat-content">
                  <div class="stat-item">
                    <span class="stat-label">{{ $t('profile.count') }}</span>
                    <span class="stat-value">{{ stat.count }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">{{ $t('profile.points') }}</span>
                    <span class="stat-value">{{ stat.point }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Activities Table -->
          <div class="activities-section" :class="{ blurred: showStatsSummary }">
            <div class="activities-header">
              <h6 class="mb-2">{{ $t('profile.activityList') }} ({{ achievementData.activities.length }})</h6>
            </div>
            <div class="activities-table-wrapper">
              <table class="modern-compact-table">
                <thead>
                  <tr>
                    <th>{{ $t('profile.type') }}</th>
                    <th>{{ $t('profile.name') }}</th>
                    <th>{{ $t('profile.date') }}</th>
                    <th>{{ $t('profile.km') }}</th>
                    <th class="d-none d-md-table-cell">{{ $t('profile.movingTime') }}</th>
                    <th>{{ $t('profile.pace') }}</th>
                    <th>{{ $t('profile.actions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="act in achievementData.activities" :key="act.id">
                    <td>
                      <font-awesome-icon icon="swimmer" v-if="act.type=='Swim'" class="activity-icon swim"/>
                      <font-awesome-icon icon="biking" v-if="act.type=='Ride' || act.type=='VirtualRide'" class="activity-icon bike"/>
                      <font-awesome-icon icon="running" v-if="act.type=='Run' || act.type=='VirtualRun'" class="activity-icon run"/>
                      <font-awesome-icon icon="walking" v-if="act.type=='Walk'" class="activity-icon walk"/>
                      <font-awesome-icon icon="puzzle-piece" v-if="act.type=='Brick2' || act.type=='Brick3'" class="activity-icon brick"/>
                    </td>
                    <td class="activity-name">{{ act.name || act.type }}</td>
                    <td>{{ formatDate(act.start_date_local) }}</td>
                    <td>{{ act.km }}</td>
                    <td class="d-none d-md-table-cell">{{ formatMovingTime(act.moving_time) }}</td>
                    <td>{{ formatPace(act) }}</td>
                    <td>
                      <button
                        class="btn-ai-generate tracklog-stats"
                        @click="showAiTracklogStatsModal(act)"
                        :title="$t('profile.aiTracklogStats')"
                      >
                        <font-awesome-icon icon="chart-line" class="btn-icon" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center py-4">
        {{ $t('profile.noAchievementData') }}
      </div>
    </b-modal>

    <!-- Avatar Upload Modal -->
    <b-modal
      id="avatar-upload-modal"
      v-model="avatarModalVisible"
      :title="$t('profile.changeAvatar')"
      size="md"
      centered
      @ok="handleAvatarUpload"
      @hidden="resetAvatarModal"
    >
      <div class="avatar-upload-content">
        <ImageUploader
          :title="$t('profile.uploadAvatar')"
          :description="$t('profile.dragDropOrClick')"
          :loading="avatarUploadLoading"
          :loadingText="$t('profile.uploadingAvatar')"
          :error="avatarUploadError"
          :maxSize="5 * 1024 * 1024"
          @file-selected="handleAvatarFileSelected"
          @file-cleared="handleAvatarFileCleared"
          @error="handleAvatarError"
          ref="avatarUploader"
        />
      </div>
    </b-modal>

    <!-- AI Gen Modal -->
    <b-modal
      id="ai-gen-modal"
      v-model="aiGenModalVisible"
      :title="$t('profile.aiGen')"
      :size="isMobile ? 'full' : 'xl'"
      centered
      scrollable
      body-class="ai-gen-modal-body"
    >
      <div v-if="aiGenLoading" class="text-center py-4">
        <font-awesome-icon icon="running" class="fa-3x running-animate" />
        <span class="ms-2">{{ $t('profile.loadingAiGen') }}</span>
      </div>
      <div v-else-if="aiGenError" class="alert alert-danger">
        {{ aiGenError }}
      </div>
      <div v-else-if="tracklogData && selectedEvent">
        <div class="ai-gen-summary">
          <div class="ai-gen-header">
            <div class="ai-gen-title">
              <h5 class="mb-0">{{ selectedEvent.name }}</h5>
            </div>
          </div>

          <!-- Tracklog Table -->
          <div class="tracklog-section">
            <div class="tracklog-header">
              <h6 class="mb-2">{{ $t('profile.tracklogList') }} ({{ tracklogData.length }})</h6>
            </div>

            <!-- Desktop Table View -->
            <div class="tracklog-table-wrapper d-none d-md-block">
              <table class="modern-compact-table">
                <thead>
                  <tr>
                    <th>{{ $t('profile.tracklogName') }}</th>
                    <th>{{ $t('profile.type') }}</th>
                    <th>{{ $t('profile.dateTime') }}</th>
                    <th>{{ $t('profile.actions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tracklog in tracklogData" :key="tracklog.id">
                    <td class="tracklog-name">{{ tracklog.name || tracklog.type }}</td>
                    <td>
                      <font-awesome-icon icon="swimmer" v-if="tracklog.type=='Swim'" class="activity-icon swim"/>
                      <font-awesome-icon icon="biking" v-if="tracklog.type=='Ride' || tracklog.type=='VirtualRide'" class="activity-icon bike"/>
                      <font-awesome-icon icon="running" v-if="tracklog.type=='Run' || tracklog.type=='VirtualRun'" class="activity-icon run"/>
                      <font-awesome-icon icon="walking" v-if="tracklog.type=='Walk'" class="activity-icon walk"/>
                      <font-awesome-icon icon="puzzle-piece" v-if="tracklog.type=='Brick2' || tracklog.type=='Brick3'" class="activity-icon brick"/>
                    </td>
                    <td>{{ formatDateTime(tracklog.start_date_local) }}</td>
                    <td>
                      <button
                        class="btn-ai-generate image-gen"
                        @click="showAiImageUploadModal(tracklog)"
                        :title="$t('profile.aiImageGen')"
                        v-b-tooltip.hover
                      >
                        <font-awesome-icon icon="image" class="btn-icon" />
                      </button>
                      <button
                        class="btn-ai-generate video-gen"
                        @click="showVideoUploadModal(tracklog)"
                        :title="$t('profile.aiVideoGen')"
                        v-b-tooltip.hover
                      >
                        <font-awesome-icon icon="video" class="btn-icon" />
                      </button>
                      <button
                        class="btn-ai-generate tracklog-stats"
                        @click="showAiTracklogStatsModal(tracklog)"
                        :title="$t('profile.aiTracklogStats')"
                        v-b-tooltip.hover
                      >
                        <font-awesome-icon icon="chart-line" class="btn-icon" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Mobile Card View -->
            <div class="tracklog-mobile-cards d-md-none">
              <div v-for="tracklog in tracklogData" :key="tracklog.id" class="tracklog-mobile-card">
                <div class="tracklog-mobile-header">
                  <div class="tracklog-mobile-info">
                    <h6 class="tracklog-mobile-name">{{ tracklog.name || tracklog.type }}</h6>
                    <div class="tracklog-mobile-meta">
                      <span class="tracklog-mobile-type">
                        <font-awesome-icon icon="swimmer" v-if="tracklog.type=='Swim'" class="activity-icon swim"/>
                        <font-awesome-icon icon="biking" v-if="tracklog.type=='Ride' || tracklog.type=='VirtualRide'" class="activity-icon bike"/>
                        <font-awesome-icon icon="running" v-if="tracklog.type=='Run' || tracklog.type=='VirtualRun'" class="activity-icon run"/>
                        <font-awesome-icon icon="walking" v-if="tracklog.type=='Walk'" class="activity-icon walk"/>
                        <font-awesome-icon icon="puzzle-piece" v-if="tracklog.type=='Brick2' || tracklog.type=='Brick3'" class="activity-icon brick"/>
                        {{ tracklog.type }}
                      </span>
                      <span class="tracklog-mobile-date">{{ formatDateTime(tracklog.start_date_local) }}</span>
                    </div>
                  </div>
                </div>
                <div class="tracklog-mobile-actions">
                  <button
                    class="btn-ai-generate-mobile image-gen"
                    @click="showAiImageUploadModal(tracklog)"
                    :title="$t('profile.aiImageGen')"
                  >
                    <font-awesome-icon icon="image" class="btn-icon" />
                    <span class="btn-text">{{ $t('profile.aiImageGen') }}</span>
                  </button>
                  <button
                    class="btn-ai-generate-mobile video-gen"
                    @click="showVideoUploadModal(tracklog)"
                    :title="$t('profile.aiVideoGen')"
                  >
                    <font-awesome-icon icon="video" class="btn-icon" />
                    <span class="btn-text">{{ $t('profile.aiVideoGen') }}</span>
                  </button>
                  <button
                    class="btn-ai-generate-mobile tracklog-stats"
                    @click="showAiTracklogStatsModal(tracklog)"
                    :title="$t('profile.aiTracklogStats')"
                  >
                    <font-awesome-icon icon="chart-line" class="btn-icon" />
                    <span class="btn-text">{{ $t('profile.aiTracklogStats') }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center py-4">
        {{ $t('profile.noTracklogData') }}
      </div>
    </b-modal>

    <!-- AI Source Upload Modal for Video Generation -->
    <b-modal
      id="ai-source-upload-modal"
      v-model="aiSourceUploadModalVisible"
      :title="$t('profile.uploadSourceImage')"
      :size="isMobile ? 'full' : 'lg'"
      centered
      @ok="handleSourceUpload"
      @cancel="cancelSourceUpload"
    >
      <ImageUploader
        :title="$t('profile.uploadSourceImage')"
        :description="$t('profile.dragDropImage')"
        :loading="sourceUploadLoading"
        :loadingText="$t('profile.uploadingImage')"
        :error="sourceUploadError"
        :maxSize="10 * 1024 * 1024"
        @file-selected="handleSourceFileSelected"
        @file-cleared="handleSourceFileCleared"
        @error="handleSourceError"
        ref="sourceUploader"
      />
    </b-modal>

    <!-- Content Preview Modal -->
    <b-modal
      id="content-preview-modal"
      v-model="contentPreviewModalVisible"
      :title="previewContentType === 'image' ? $t('profile.aiGeneratedImage') : $t('profile.aiGeneratedVideo')"
      :size="isMobile ? 'full' : 'xl'"
      :centered="!isMobile"
      hide-footer
      :no-close-on-backdrop="false"
      :no-close-on-esc="false"
    >
      <div class="preview-container">
        <!-- Image Preview -->
        <div v-if="previewContentType === 'image'" class="image-preview">
          <div v-if="isMobile && !imageZoomed" class="zoom-hint">
            <small class="text-muted">{{ $t('profile.tapToZoom') }}</small>
          </div>
          <img
            :src="previewContentUrl"
            :alt="$t('profile.aiGeneratedImage')"
            :class="['preview-image', { 'zoomed': imageZoomed }]"
            @error="onPreviewError"
            @load="onPreviewLoad"
            @click="toggleImageZoom"
          />
        </div>

        <!-- Video Preview -->
        <div v-else-if="previewContentType === 'video'" class="video-preview">
          <video
            :src="previewContentUrl"
            controls
            class="preview-video"
            @error="onPreviewError"
            @loadeddata="onPreviewLoad"
          >
            {{ $t('profile.videoNotSupported') }}
          </video>
        </div>

        <!-- Error State -->
        <div v-if="previewError" class="preview-error">
          <font-awesome-icon icon="exclamation-triangle" class="fa-2x text-warning" />
          <p class="mt-2">{{ $t('profile.failedToLoadContent') }}</p>
          <a :href="previewContentUrl" target="_blank" class="btn btn-outline-primary mt-2">
            <font-awesome-icon icon="external-link-alt" />
            {{ $t('profile.openInNewTab') }}
          </a>
        </div>

        <!-- Action Buttons -->
        <div v-if="!previewLoading && !previewError" class="preview-actions mt-3">
          <button
            class="btn btn-primary me-2"
            @click="downloadContent"
          >
            <font-awesome-icon :icon="previewContentType === 'image' ? 'download' : 'download'" />
            {{ previewContentType === 'image' ? $t('profile.downloadImage') : $t('profile.downloadVideo') }}
          </button>
          <button
            class="btn btn-outline-primary"
            @click="openInNewTab"
          >
            <font-awesome-icon icon="external-link-alt" />
            {{ $t('profile.openInNewTab') }}
          </button>
        </div>
      </div>
    </b-modal>

    <!-- AI Image Upload Modal for Image Generation -->
    <b-modal
      id="ai-image-upload-modal"
      v-model="aiImageUploadModalVisible"
      :title="$t('profile.uploadImageForAiImage')"
      :size="isMobile ? 'full' : 'lg'"
      centered
      @ok="handleAiImageUpload"
      @cancel="cancelAiImageUpload"
    >
      <ImageUploader
        :title="$t('profile.uploadImageForAiImage')"
        :description="$t('profile.dragDropImageForAiImage')"
        :loading="aiImageUploadLoading"
        :loadingText="$t('profile.uploadingImage')"
        :error="aiImageUploadError"
        :maxSize="10 * 1024 * 1024"
        @file-selected="handleAiImageFileSelected"
        @file-cleared="handleAiImageFileCleared"
        @error="handleAiImageError"
        ref="aiImageUploader"
      />
    </b-modal>

    <!-- AI Generated Content Modal -->
    <b-modal
      id="ai-image-modal"
      v-model="aiImageModalVisible"
      :title="currentGenerationType === 'video' ? $t('profile.aiGeneratedVideo') : $t('profile.aiGeneratedImage')"
      size="xl"
      centered
      scrollable
      body-class="ai-image-modal-body"
    >
      <div v-if="aiImageLoading" class="text-center py-4">
        <font-awesome-icon icon="robot" class="fa-3x running-animate" />
        <span class="ms-2">{{ currentGenerationType === 'video' ? $t('profile.generatingVideo') : $t('profile.generatingImage') }}</span>
      </div>
      <div v-else-if="aiImageError" class="alert alert-danger">
        {{ aiImageError }}
      </div>
      <div v-else-if="generatedImageUrl || generatedVideoUrl" class="ai-content-container">
        <div class="ai-content-header mb-3">
          <h6 class="mb-1">{{ $t('profile.aiGeneratedFor') }}: {{ selectedTracklogName }}</h6>
          <small class="text-muted">{{ $t('profile.generatedOn') }}: {{ new Date().toLocaleString() }}</small>
        </div>

        <!-- Image Display -->
        <div v-if="generatedImageUrl && currentGenerationType === 'image'" class="ai-image-wrapper">
          <img
            :src="generatedImageUrl"
            :alt="$t('profile.aiGeneratedImage')"
            class="ai-generated-image"
            @load="onImageLoad"
            @error="onImageError"
          />
        </div>

        <!-- Video Display -->
        <div v-else-if="generatedVideoUrl && currentGenerationType === 'video'" class="ai-video-wrapper">
          <video
            :src="generatedVideoUrl"
            controls
            preload="metadata"
            class="ai-generated-video"
            @loadedmetadata="onVideoLoad"
            @error="onVideoError"
          >
            {{ $t('profile.videoNotSupported') }}
          </video>
        </div>
        <div class="ai-content-actions mt-3">
          <button v-if="currentGenerationType === 'image'" class="btn btn-primary me-2" @click="downloadImage">
            <font-awesome-icon icon="download" class="me-1" />
            {{ $t('profile.downloadImage') }}
          </button>
          <button v-else-if="currentGenerationType === 'video'" class="btn btn-primary me-2" @click="downloadVideo">
            <font-awesome-icon icon="download" class="me-1" />
            {{ $t('profile.downloadVideo') }}
          </button>

          <button class="btn btn-outline-primary me-2" @click="openContentInNewTab">
            <font-awesome-icon icon="external-link-alt" class="me-1" />
            {{ $t('profile.openInNewTab') }}
          </button>
          <button class="btn btn-outline-secondary me-2" @click="shareContent">
            <font-awesome-icon icon="share" class="me-1" />
            {{ currentGenerationType === 'video' ? $t('profile.shareVideo') : $t('profile.shareImage') }}
          </button>
          <button
            :class="['btn', 'me-2', currentContentPrivacy ? 'btn-warning' : 'btn-success']"
            @click="toggleContentPrivacy"
            :title="currentContentPrivacy ? $t('profile.makePublic') : $t('profile.onlyMe')"
          >
            <font-awesome-icon :icon="currentContentPrivacy ? 'eye-slash' : 'eye'" class="me-1" />
            {{ currentContentPrivacy ? $t('profile.makePublic') : $t('profile.onlyMe') }}
          </button>
        </div>
      </div>
    </b-modal>

    <!-- AI Analysis Modal -->
    <b-modal
      id="ai-analysis-modal"
      :title="$t('profile.aiTracklogStats')"
      size="lg"
      centered
      scrollable
      hide-footer
    >
      <div v-if="aiAnalysisData && !aiAnalysisLoading" class="ai-analysis-content">
        <!-- Current Activity Info -->
        <div class="current-activity-info mb-4">
          <h5 class="text-primary">
            <font-awesome-icon icon="running" class="me-2" />
            Current Activity
          </h5>
          <div class="activity-details">
            <p><strong>{{ aiAnalysisData.current_activity.name }}</strong></p>
            <div class="activity-stats">
              <span class="badge badge-info me-2">{{ aiAnalysisData.current_activity.type }}</span>
              <span class="badge badge-secondary me-2">{{ (aiAnalysisData.current_activity.distance / 1000).toFixed(2) }} km</span>
              <span class="badge badge-secondary me-2">{{ Math.floor(aiAnalysisData.current_activity.moving_time / 60) }} min</span>
            </div>
          </div>
        </div>

        <!-- AI Analysis -->
        <div class="ai-analysis-text">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="text-success mb-0">
              <font-awesome-icon icon="brain" class="me-2" />
              AI Performance Analysis
            </h5>
            <div class="d-flex gap-2">
              <b-button
                variant="outline-primary"
                size="sm"
                @click.prevent="shareToStrava"
                :disabled="!aiAnalysisData || !aiAnalysisData.analysis || shareToStravaLoading"
              >
                <span v-if="shareToStravaLoading" class="spinner-border spinner-border-sm me-1"></span>
                <font-awesome-icon v-else :icon="['fab', 'strava']" class="me-1" />
                {{ shareToStravaLoading ? 'Sharing...' : 'Share to Strava' }}
              </b-button>
              <b-button
                variant="outline-success"
                size="sm"
                @click="copyAnalysisText"
                :disabled="!aiAnalysisData.analysis"
              >
                <font-awesome-icon icon="copy" class="me-1" />
                Copy
              </b-button>
            </div>
          </div>

          <!-- Share Status Messages -->
          <div v-if="shareToStravaError" class="alert alert-danger mt-3" role="alert">
            <font-awesome-icon icon="exclamation-triangle" class="me-2" />
            {{ shareToStravaError }}
          </div>
          <div v-if="shareToStravaSuccess" class="alert alert-success mt-3" role="alert">
            <div class="d-flex align-items-start">
              <font-awesome-icon icon="check-circle" class="me-2 mt-1" />
              <div class="flex-grow-1">
                <div>{{ shareToStravaSuccess }}</div>
                <a
                  v-if="sharedStravaUrl"
                  :href="sharedStravaUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn btn-sm btn-outline-success mt-2"
                >
                  <font-awesome-icon :icon="['fab', 'strava']" class="me-1" />
                  View on Strava
                  <font-awesome-icon icon="external-link-alt" class="ms-1" size="xs" />
                </a>
              </div>
            </div>
          </div>

          <div class="analysis-content">
            <div class="analysis-text" v-html="renderMarkdown(aiAnalysisData.analysis)"></div>
          </div>
        </div>

        <!-- Analysis Stats -->
        <div class="analysis-stats mt-4">
          <div class="row">
            <div class="col-md-6">
              <div class="stat-card">
                <div class="stat-label">{{ $t('profile.comparisonPeriod') }}</div>
                <div class="stat-value">{{ aiAnalysisData.comparison_period }}</div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="stat-card">
                <div class="stat-label">{{ $t('profile.activitiesAnalyzed') }}</div>
                <div class="stat-value">{{ aiAnalysisData.total_activities_analyzed }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading Animation -->
      <div v-if="aiAnalysisLoading" class="text-center p-5">
        <div class="ai-loading-animation">
          <font-awesome-icon icon="running" class="fa-3x text-primary mb-3 running-animate" />
          <h5 class="mt-3 text-primary">{{ $t('profile.aiAnalysisThinking') }}</h5>
          <p class="text-muted">{{ $t('profile.aiAnalysisThinkingDescription') }}</p>
        </div>
      </div>

      <!-- Error Message -->
      <div v-else-if="aiAnalysisError" class="alert alert-danger">
        {{ aiAnalysisError }}
      </div>
    </b-modal>

    <!-- Content Text Modal -->
    <b-modal
      id="content-text-modal"
      v-model="showContentTextModal"
      :title="selectedContentTitle"
      size="lg"
      centered
      scrollable
      hide-footer
    >
      <template #modal-header="{ close }">
        <div class="d-flex justify-content-between align-items-center w-100">
          <h5 class="modal-title">{{ selectedContentTitle }}</h5>
          <div>
            <b-button
              v-if="selectedContentActivityId"
              variant="outline-primary"
              size="sm"
              @click="shareContentToStrava"
              :disabled="!selectedContentText || shareToStravaLoading"
              class="me-2"
            >
              <span v-if="shareToStravaLoading" class="spinner-border spinner-border-sm me-1"></span>
              <font-awesome-icon v-else :icon="['fab', 'strava']" class="me-1" />
              {{ shareToStravaLoading ? 'Sharing...' : 'Share to Strava' }}
            </b-button>
            <b-button
              variant="outline-primary"
              size="sm"
              @click="copyContentText"
              :disabled="!selectedContentText"
              class="me-2"
            >
              <font-awesome-icon icon="copy" class="me-1" />
              Copy
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              @click="close()"
            >
              <font-awesome-icon icon="times" />
            </b-button>
          </div>
        </div>
      </template>

      <!-- Strava link after successful share -->
      <div v-if="sharedStravaUrl" class="alert alert-success mb-3" role="alert">
        <font-awesome-icon :icon="['fab', 'strava']" class="me-2" />
        <strong>Shared successfully!</strong>
        <a :href="sharedStravaUrl" target="_blank" rel="noopener noreferrer" class="ms-2">
          View on Strava
          <font-awesome-icon icon="external-link-alt" class="ms-1" />
        </a>
      </div>

      <div class="content-text-modal-body">
        <div v-if="selectedContentText" v-html="renderMarkdown(selectedContentText)" class="markdown-content-full"></div>
      </div>
    </b-modal>

    <!-- No Activities Modal -->
    <b-modal
      id="too-few-activities-modal"
      v-model="displayNeedSyncMore"
      :title="$t('profile.tooFewActivities')"
      :size="isMobile ? 'sm' : 'md'"
      centered
      hide-footer
      @hidden="displayNeedSyncMore = false"
    >
      <div class="too-few-activities-modal-content text-center">
        <div class="modal-icon mb-3">
          <font-awesome-icon icon="running" class="fa-3x text-muted" />
        </div>
        <div class="modal-message mb-4">
          <p class="lead mb-3">{{ $t('profile.tooFewActivitiesMessage') }}</p>
          <div class="modal-features mb-4">
            <div class="feature-item mb-2">
              <font-awesome-icon icon="sync-alt" class="text-primary me-2" />
              <span>{{ $t('profile.tooFewActivitiesModalSyncFeature') }}</span>
            </div>
            <div class="feature-item mb-2">
              <font-awesome-icon icon="calendar-alt" class="text-success me-2" />
              <span>{{ $t('profile.tooFewActivitiesModalJoinEventsFeature') }}</span>
            </div>
            <div class="feature-item mb-2">
              <font-awesome-icon icon="chart-line" class="text-info me-2" />
              <span>{{ $t('profile.tooFewActivitiesModalTrackProgressFeature') }}</span>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <div class="d-grid gap-2">
            <button
              @click="syncAllActivities"
              class="btn btn-primary btn-lg"
              :disabled="tooFewActivitiesModalSyncing"
            >
              <span v-if="tooFewActivitiesModalSyncing" class="spinner-border spinner-border-sm me-2"></span>
              <font-awesome-icon v-else icon="sync-alt" class="me-2" />
              {{ tooFewActivitiesModalSyncing ? $t('profile.tooFewActivitiesModalSyncing') : $t('profile.tooFewActivitiesModalSyncActivities') }}
            </button>
            <button
              @click="navigateToEvents"
              class="btn btn-outline-primary"
            >
              <font-awesome-icon icon="search" class="me-2" />
              {{ $t('profile.tooFewActivitiesModalExploreEvents') }}
            </button>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Progress Tracker Modal -->
    <b-modal
      v-model="showProgressModal"
      size="xl"
      title="My Progress Tracker"
      hide-footer
      centered
      modal-class="progress-modal"
    >
      <ProgressTracker
        :progress-data="progressData"
        :loading="progressLoading"
        :error="progressError"
        :metrics="progressMetrics"
        :breakdown="activityBreakdown"
        :comparison="comparisonData"
        :selected-timeframe="selectedTimeframe"
        :selected-event="selectedEvent"
        :event-progress="eventProgress"
        :days-remaining="daysRemaining"
        @timeframe-change="handleProgressTimeframeChange"
      />
    </b-modal>

    <!-- Error Message -->
    <div v-if="error" class="alert alert-danger mt-4" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>
  </div>
</template>

<script>
import $backend from '../backend'
import config from '../config'
import AchievementChart from '../components/AchievementChart.vue'
import ImageUploader from '../components/ImageUploader.vue'
import ProgressTracker from '../components/profile/ProgressTracker.vue'
import SubscriptionPricing from '../components/SubscriptionPricing.vue'
import progressMixin from '../mixins/progressMixin'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faBrain, faSpinner, faRunning, faExclamationTriangle, faCopy, faTimes, faMountain, faRoute, faHeartbeat, faStopwatch, faHourglassHalf } from '@fortawesome/free-solid-svg-icons'
import { library } from '@fortawesome/fontawesome-svg-core'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import polyline from 'polyline-encoded'

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
})

// Add icons to library
library.add(faBrain, faSpinner, faRunning, faExclamationTriangle, faCopy, faTimes, faMountain, faRoute, faHeartbeat, faStopwatch, faHourglassHalf)

export default {
  components: {
    AchievementChart,
    ImageUploader,
    ProgressTracker,
    SubscriptionPricing,
    FontAwesomeIcon
  },
  mixins: [progressMixin],
  name: 'Profile',
  metaInfo () {
    return {
      title: this.$t('profile.myChallenges')
    }
  },

  data () {
    return {
      // title is not needed in data, use $t directly in metaInfo
      athlete: {},
      events: [],
      error: '',
      email: '',
      emailState: null,
      // Profile configuration fields
      age: '',
      ageState: null,
      weight: '',
      weightState: null,
      height: '',
      heightState: null,
      sex: '',
      sexState: null,
      autoAnalyseAndShare: false,
      // Training target fields
      targetFinishTimeHours: '',
      targetFinishTimeMinutes: '',
      targetRaceDate: '',
      targetSportTypes: [], // Array of selected sport types
      goalDescription: '', // Goal description text
      // Profile update status
      profileUpdateError: null,
      profileUpdateSuccess: null,
      profileUpdateLoading: false,
      totalActivities: 0,
      paginator: {
        currentPage: 1,
        totalPages: 1,
        limit: 6,
        count: 0
      },
      loadingMore: false,
      allLoaded: false,
      achievementModalVisible: false,
      achievementLoading: false,
      achievementError: '',
      achievementData: null,
      selectedEvent: null,
      showStatsSummary: false,
      athleteStats: {},
      chartType: 'bar',
      avatarModalVisible: false,
      avatarUploadLoading: false,
      avatarUploadError: '',
      avatarSelectedFile: null,
      aiGenModalVisible: false,
      aiGenLoading: false,
      aiGenError: '',
      tracklogData: null,
      aiImageModalVisible: false,
      aiImageLoading: false,
      aiImageError: '',
      generatedImageUrl: '',
      generatedVideoUrl: '',
      currentGenerationType: 'image',
      selectedTracklogName: '',
      aiSourceUploadModalVisible: false,
      sourceUploadLoading: false,
      sourceUploadError: '',
      sourceSelectedFile: null,
      pendingVideoGeneration: null,
      aiImageUploadModalVisible: false,
      aiImageUploadLoading: false,
      aiImageUploadError: '',
      // No activities modal
      displayNeedSyncMore: false,
      tooFewActivitiesModalSyncing: false,
      backgroundSyncInProgress: false,
      aiImageSelectedFile: null,
      pendingImageGeneration: null,
      currentContentPrivacy: false,

      // Content Preview Modal
      contentPreviewModalVisible: false,
      previewContentUrl: '',
      previewContentType: '',
      previewLoading: false,
      previewError: false,
      imageZoomed: false,
      currentActivityId: null,
      // Progress Tracker Modal
      showProgressModal: false,
      // Tab management
      activeTab: 'my-activities',
      // AI History data
      aiHistoryData: null,
      aiHistoryLoading: false,
      aiHistoryError: '',
      aiHistoryPagination: {
        currentPage: 1,
        limit: 5,
        totalCount: 0
      },
      // Privacy management
      privacyLoading: {},
      // AI Analysis Modal
      aiAnalysisData: null,
      aiAnalysisLoading: false,
      aiAnalysisError: null,
      // Share to Strava status
      shareToStravaLoading: false,
      shareToStravaError: null,
      shareToStravaSuccess: null,
      sharedStravaUrl: null,
      // Content text modal
      showContentTextModal: false,
      selectedContentText: '',
      selectedContentTitle: '',
      selectedContentActivityId: null,
      // Polling management
      isPolling: false,
      pollingInterval: null,
      pollingIntervalMs: 10000, // 10 seconds
      lastPollingTime: null,
      pollingError: null,
      pollingErrorCount: 0,
      // Activities data
      activitiesData: [],
      activitiesLoading: false,
      activitiesLoadingMore: false,
      activitiesError: null,
      activitiesPagination: {
        currentPage: 1,
        limit: 20,
        totalCount: 0,
        hasMore: true
      },
      activitiesHasMore: true,
      activityMaps: {},
      // Flag to prevent multiple redirects
      hasRedirected: false,
      // Subscription data
      subscriptionData: null,
      subscriptionLoading: false,
      isPro: false
    }
  },

  created () {
    // Only initialize if we're on the profile route
    if (this.$route.path === '/profile') {
      this.initializeAthleteData()
    }
  },
  mounted () {
    // Don't do anything if we're not on the profile route
    if (this.$route.path !== '/profile') {
      return
    }

    window.addEventListener('scroll', this.handleScroll)
    window.addEventListener('resize', this.handleWindowResize)

    // Fetch fresh athlete data from database and update localStorage
    if (this.athlete && this.athlete._id && this.athlete.token && this.athlete.token.access_token) {
      this.fetchAndUpdateAthleteProfile()
      this.fetchSubscriptionStatus()

      this.$nextTick(() => {
        this.loadActivities()
      })
    } else {
      // No valid athlete data or token, redirect to login
      this.redirectToLoginWithCurrentUrl()
    }

    // Maps will be initialized when activities are loaded

    // Check for pending Strava share after OAuth callback
    this.checkPendingStravaShare()
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleWindowResize)
    this.stopPolling()
    this.destroyActivityMaps()
  },

  computed: {
    isLoggedIn: function () {
      return this.$store.state.isLoggedIn
    },
    isMobile: function () {
      return window.innerWidth <= 768
    },
    minRaceDate: function () {
      // Get today's date in YYYY-MM-DD format for the date input min attribute
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  },

  watch: {
    // Watch for route changes to reset redirect flag
    '$route' (to, from) {
      if (to.path === '/profile' && from.path === '/login') {
        // User just came from login page, reset redirect flag
        this.hasRedirected = false
      }
    },
    athlete: {
      handler (newAthlete) {
        // Don't process athlete changes if we're on login page
        if (this.$route.path === '/login') {
          return
        }

        // Only load activities if we have valid athlete data and no activities loaded yet
        if (newAthlete && newAthlete._id && newAthlete.token && newAthlete.token.access_token && this.activitiesData.length === 0 && !this.activitiesLoading) {
          this.$nextTick(() => {
            this.loadActivities()
          })
        }
        // Remove the redirect logic from watcher - let the mounted hook handle initial validation
      },
      immediate: true
    },
    activeTab (newTab) {
      if (newTab === 'my-activities' && this.activitiesData.length === 0 && !this.activitiesLoading) {
        this.loadActivities()
      } else if (newTab === 'my-activities' && this.activitiesData.length > 0) {
        // Reinitialize maps when switching to activities tab (in case maps were destroyed)
        this.$nextTick(() => {
          setTimeout(() => {
            this.initializeActivityMaps()
          }, 300)
        })
      } else if (newTab === 'ai-history' && !this.aiHistoryData) {
        this.loadAiHistory()
      }
    }
  },

  methods: {
    redirectToStravaAuth () {
      try {
        const authUrl = config.getStravaAuthUrl('write')
        console.log('Redirecting to Strava:', authUrl)

        // Redirect after short delay to show message
        setTimeout(() => {
          window.location.href = authUrl
        }, 1500)
      } catch (error) {
        console.error('Error getting auth URL:', error)
        // Fallback to login page (only if not already on login page)
        if (this.$route.path !== '/login') {
          this.$router.push('/login')
        }
      }
    },
    // Initialize athlete data from localStorage and validate token
    initializeAthleteData () {
      // Don't initialize if we're on login page
      if (this.$route.path === '/login') {
        return
      }

      if (localStorage.hasOwnProperty('athlete')) {
        const athleteData = localStorage.getItem('athlete')
        if (athleteData && athleteData !== 'undefined') {
          try {
            this.athlete = JSON.parse(athleteData)

            // Validate that athlete has required data and token
            if (this.athlete && this.athlete._id && this.athlete.token && this.athlete.token.access_token) {
              this.title = 'Profile | ' + this.athlete.firstname + ' ' + this.athlete.lastname
              this.$store.commit('setIsLoggedIn', true)
              // Reset redirect flag since we have valid data
              this.hasRedirected = false
            } else {
              // Invalid athlete data, clear it and redirect to login
              console.log('Invalid athlete data: missing required fields or token')
              this.clearAthleteDataAndRedirect()
            }
          } catch (error) {
            console.error('Error parsing athlete data:', error)
            this.clearAthleteDataAndRedirect()
          }
        } else {
          // No valid athlete data
          this.clearAthleteDataAndRedirect()
        }
      } else {
        // No athlete data at all
        this.clearAthleteDataAndRedirect()
      }
    },

    // Clear athlete data and redirect to login
    clearAthleteDataAndRedirect () {
      localStorage.removeItem('athlete')
      this.$store.commit('setIsLoggedIn', false)
      this.redirectToLoginWithCurrentUrl()
    },

    // Fetch athlete profile from database and update localStorage
    async fetchAndUpdateAthleteProfile () {
      if (!this.athlete || !this.athlete._id || !this.athlete.token || !this.athlete.token.access_token) {
        console.log('No valid athlete data to fetch profile')
        return
      }

      try {
        console.log('Fetching fresh athlete profile from database...')
        const response = await $backend.getAthleteProfile(this.athlete._id, this.athlete.token.access_token)

        if (response && response.data) {
          const freshAthleteData = response.data

          // Update local component data
          this.athlete = freshAthleteData

          // Update localStorage with fresh data
          localStorage.setItem('athlete', JSON.stringify(freshAthleteData))

          console.log('Athlete profile updated successfully from database')
        }
      } catch (error) {
        console.error('Error fetching athlete profile:', error)

        // Handle unauthorized error
        if (error.msg === 'unauthorized' || (error.response && error.response.status === 401)) {
          this.handleUnauthorizedError()
        }
      }
    },

    // Fetch subscription status
    async fetchSubscriptionStatus () {
      if (!this.athlete || !this.athlete._id || !this.athlete.token || !this.athlete.token.access_token) {
        console.log('No valid athlete data to fetch subscription')
        return
      }

      try {
        this.subscriptionLoading = true
        console.log('Fetching subscription status...')

        const response = await $backend.getAthleteSubscription(this.athlete._id, this.athlete.token.access_token)

        if (response && response.data) {
          this.subscriptionData = response.data
          this.isPro = response.data.is_pro || false

          console.log('Subscription status:', this.isPro ? 'PRO' : 'Free')
        }
      } catch (error) {
        console.error('Error fetching subscription status:', error)

        // Don't show error if subscription not found - user just doesn't have subscription
        if (error.msg === 'subscription not found' || (error.response && error.response.status === 404)) {
          this.isPro = false
          this.subscriptionData = null
        } else if (error.msg === 'unauthorized' || (error.response && error.response.status === 401)) {
          this.handleUnauthorizedError()
        }
      } finally {
        this.subscriptionLoading = false
      }
    },

    // Redirect to login while preserving current URL
    redirectToLoginWithCurrentUrl () {
      // Don't redirect if already on login page or already redirected
      if (this.$route.path === '/login' || this.hasRedirected) {
        return
      }

      // Mark that we've redirected to prevent multiple redirects
      this.hasRedirected = true

      // Save current URL for redirect after successful login
      const currentUrl = this.$route.fullPath
      localStorage.setItem('redirectUrl', currentUrl)

      // Set error message to display on page
      this.activitiesError = 'Authentication required. Redirecting to login...'

      // Redirect to login page
      this.$router.push('/login')
    },

    // Utility method to handle unauthorized errors consistently
    handleUnauthorizedError () {
      // Don't redirect if already on login page
      if (this.$route.path === '/login') {
        return
      }

      // Save current URL for redirect after successful login
      const currentUrl = this.$route.fullPath
      localStorage.setItem('redirectUrl', currentUrl)

      // Remove athlete data and update store state
      localStorage.removeItem('athlete')
      this.$store.commit('setIsLoggedIn', false)

      // Set error message to display on page
      this.activitiesError = this.$t('profile.sessionExpired') || 'Session expired. Please login again.'

      // Redirect to login page
      this.$router.push('/login')
    },

    redirectToStravaLogin () {
      // Save current URL for redirect after successful login
      const currentUrl = this.$route.fullPath
      localStorage.setItem('redirectUrl', currentUrl)
      console.log('Saving redirect URL:', currentUrl)

      // Show inline message first
      this.profileUpdateError = this.$t('profile.redirectingToLogin')

      // Load config and redirect to Strava OAuth URL
      this.redirectToStravaAuth()
    },

    // Utility method to show error toast
    showErrorToast (message) {
      this.$bvToast.toast(message, {
        title: this.$t('profile.error'),
        variant: 'danger',
        solid: true,
        autoHideDelay: 5000,
        appendToast: true
      })
    },

    // Progress Tracker Methods
    openProgressModal () {
      console.log('Opening progress tracker modal...')
      this.showProgressModal = true

      // Load progress data if not already loaded
      if (!this.progressData) {
        console.log('Loading progress data...')
        this.loadProgressData()
      }
    },

    handleProgressTimeframeChange (timeframe) {
      console.log('Progress timeframe changed to:', timeframe)
      this.setTimeframe(timeframe)
    },

    // Render markdown text to HTML
    renderMarkdown (text) {
      if (!text) return ''

      // Simple markdown rendering - you can replace this with a proper markdown library
      return text
        // Headers
        .replace(/^### (.*$)/gim, '<h3>$1</h3>')
        .replace(/^## (.*$)/gim, '<h2>$1</h2>')
        .replace(/^# (.*$)/gim, '<h1>$1</h1>')
        // Bold
        .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
        // Italic
        .replace(/\*(.*)\*/gim, '<em>$1</em>')
        // Line breaks
        .replace(/\n/gim, '<br>')
        // Lists
        .replace(/^\* (.*$)/gim, '<li>$1</li>')
        .replace(/(<li>.*<\/li>)/gims, '<ul>$1</ul>')
        // Numbers
        .replace(/^\d+\. (.*$)/gim, '<li>$1</li>')
        .replace(/(<li>.*<\/li>)/gims, (match) => {
          if (!match.includes('<ul>')) {
            return '<ol>' + match + '</ol>'
          }
          return match
        })
    },

    loadChallenge (page = 1, append = false) {
      if (this.loadingMore || this.allLoaded) return
      if (!this.athlete || !this.athlete._id) {
        this.error = this.$t('profile.athleteNotFound')
        return
      }
      this.loadingMore = true
      const limit = this.paginator.limit
      const offset = (page - 1) * limit
      $backend.getAthChallenge(this.athlete, limit, offset).then(responseData => {
        const newEvents = responseData.data
        if (append) {
          this.events = this.events.concat(newEvents)
        } else {
          this.events = newEvents
        }
        this.paginator.count = responseData.count
        this.totalActivities = responseData.count
        this.paginator.totalPages = Math.ceil(this.paginator.count / this.paginator.limit)
        this.paginator.currentPage = page
        if (this.events.length >= this.paginator.count) {
          this.allLoaded = true
        }
        this.loadingMore = false
      }).catch(error => {
        this.error = error.data.msg
        this.loadingMore = false

        // Handle unauthorized error
        if (error.data.msg === 'unauthorized') {
          this.handleUnauthorizedError()
        }
      })
    },

    loadMore () {
      if (this.loadingMore || this.allLoaded) return
      const nextPage = this.paginator.currentPage + 1
      this.loadChallenge(nextPage, true)
    },

    handleScroll () {
      if (this.loadingMore || this.allLoaded) return
      const scrollY = window.scrollY || window.pageYOffset
      const visible = window.innerHeight
      const pageHeight = document.documentElement.scrollHeight
      if (scrollY + visible + 200 >= pageHeight) {
        this.loadMore()
      }
    },

    logout () {
      localStorage.removeItem('athlete')
      // Only redirect to login if not already on login page
      if (this.$route.path !== '/login') {
        this.$router.push('/login')
      }
    },

    navigateToEvents () {
      this.displayNeedSyncMore = false
      this.$router.push('/events')
    },

    syncAllActivities () {
      if (!this.athlete || !this.athlete._id || !this.athlete.token || !this.athlete.token.access_token) {
        this.$bvToast.toast(this.$t('profile.athleteNotFound'), {
          title: this.$t('profile.error'),
          variant: 'danger',
          solid: true
        })
        return
      }

      this.tooFewActivitiesModalSyncing = true

      $backend.syncAthleteActivities(this.athlete._id, this.athlete)
        .then(response => {
          // Check if response indicates background processing (HTTP 202)
          if (response.status === 'processing' || response.msg === 'Activity sync started in background') {
            // Close modal immediately
            this.displayNeedSyncMore = false

            // Set flag to show sync message in empty state
            this.backgroundSyncInProgress = true
          } else {
            // Old behavior for synchronous sync (if any)
            this.$bvToast.toast(this.$t('profile.tooFewActivitiesModalSyncSuccess'), {
              title: this.$t('profile.success'),
              variant: 'success',
              solid: true
            })

            // Close modal and reload activities
            this.displayNeedSyncMore = false
            this.loadActivities()
          }
        })
        .catch(error => {
          console.error('Error syncing activities:', error)
          this.$bvToast.toast(this.$t('profile.tooFewActivitiesModalSyncError'), {
            title: this.$t('profile.error'),
            variant: 'danger',
            solid: true
          })
        })
        .finally(() => {
          this.tooFewActivitiesModalSyncing = false
        })
    },

    sync () {
      if (!this.athlete || !this.athlete.token || !this.athlete.token.access_token) {
        this.error = this.$t('profile.athleteNotFound')
        return
      }
      let token = this.athlete.token.access_token
      let d = new Date()
      let afterTs = d.setDate(d.getDate() - 5)
      $backend.sync(token, afterTs).then(responseData => {
      }).catch(error => {
        this.error = error.data.msg

        // Handle unauthorized error
        if (error.data.msg === 'unauthorized') {
          this.handleUnauthorizedError()
        }
      })
    },

    checkFormValidity () {
      // All fields are optional, only validate if they have values

      // Validate email only if it has a value
      if (this.email && this.email.trim()) {
        this.emailState = !!this.isValidEmail(this.email)
      } else {
        this.emailState = null // Empty is valid
      }

      // Validate age only if it has a value
      if (this.age) {
        this.ageState = !!this.isValidAge(this.age)
      } else {
        this.ageState = null // Empty is valid
      }

      // Validate weight only if it has a value
      if (this.weight) {
        this.weightState = !!this.isValidWeight(this.weight)
      } else {
        this.weightState = null // Empty is valid
      }

      // Validate height only if it has a value
      if (this.height) {
        this.heightState = !!this.isValidHeight(this.height)
      } else {
        this.heightState = null // Empty is valid
      }

      // Sex/Gender is always optional
      this.sexState = null

      // Only check that no field has an invalid state (false)
      // null (empty) and true (valid) are both acceptable
      return this.emailState !== false &&
             this.ageState !== false &&
             this.weightState !== false &&
             this.heightState !== false
    },

    isValidEmail (email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },

    isValidAge (age) {
      const ageNum = parseInt(age)
      return !isNaN(ageNum) && ageNum >= 1 && ageNum <= 120
    },

    isValidWeight (weight) {
      const weightNum = parseFloat(weight)
      return !isNaN(weightNum) && weightNum >= 1 && weightNum <= 500
    },

    isValidHeight (height) {
      const heightNum = parseFloat(height)
      return !isNaN(heightNum) && heightNum >= 50 && heightNum <= 300
    },

    resetModal () {
      console.log('resetModal called - Opening edit profile modal')

      // Always reload athlete data from localStorage when modal opens
      const athleteData = localStorage.getItem('athlete')
      console.log('localStorage athlete data:', athleteData ? 'Found' : 'Not found')

      if (athleteData && athleteData !== 'undefined') {
        try {
          this.athlete = JSON.parse(athleteData)
          console.log('✅ Loaded fresh athlete data from DB:', {
            _id: this.athlete._id,
            email: this.athlete.email,
            age: this.athlete.age,
            weight: this.athlete.weight,
            height: this.athlete.height,
            sex: this.athlete.sex,
            autoAnalyseAndShare: this.athlete.autoAnalyseAndShare || this.athlete.auto_analyse_and_share,
            targetFinishTime: this.athlete.targetFinishTime || this.athlete.target_finish_time,
            targetRaceDate: this.athlete.targetRaceDate || this.athlete.target_race_date
          })
          console.log('Mapping: DB sex "' + this.athlete.sex + '" -> Form value will be set')
        } catch (e) {
          console.error('❌ Error parsing athlete data:', e)
          // Keep existing athlete data if parse fails
        }
      } else {
        console.warn('⚠️ No athlete data found in localStorage')
      }

      // Populate form fields from athlete data
      this.email = (this.athlete && this.athlete.email) ? this.athlete.email : ''
      this.emailState = null

      // Reset new profile fields
      this.age = (this.athlete && this.athlete.age) ? String(this.athlete.age) : ''
      this.ageState = null
      this.weight = (this.athlete && this.athlete.weight) ? String(this.athlete.weight) : ''
      this.weightState = null
      this.height = (this.athlete && this.athlete.height) ? String(this.athlete.height) : ''
      this.heightState = null

      // Map DB sex values (M/F/"") to form values (male/female/"")
      if (this.athlete && this.athlete.sex === 'M') {
        this.sex = 'male'
      } else if (this.athlete && this.athlete.sex === 'F') {
        this.sex = 'female'
      } else {
        this.sex = '' // Empty or missing = empty string (unknown)
      }
      this.sexState = null

      // Handle autoAnalyseAndShare (support both camelCase and snake_case from backend)
      this.autoAnalyseAndShare = (this.athlete && (this.athlete.autoAnalyseAndShare || this.athlete.auto_analyse_and_share))
        ? (this.athlete.autoAnalyseAndShare || this.athlete.auto_analyse_and_share)
        : false

      // Parse and populate target finish time (stored as total minutes)
      // Support both camelCase and snake_case from backend
      const targetFinishTime = this.athlete && (this.athlete.targetFinishTime || this.athlete.target_finish_time)
      if (targetFinishTime) {
        const totalMinutes = targetFinishTime
        this.targetFinishTimeHours = Math.floor(totalMinutes / 60)
        this.targetFinishTimeMinutes = totalMinutes % 60
      } else {
        this.targetFinishTimeHours = ''
        this.targetFinishTimeMinutes = ''
      }

      // Populate target race date (support both camelCase and snake_case from backend)
      // Convert Unix timestamp to YYYY-MM-DD format for date input
      const targetRaceDateTimestamp = this.athlete && (this.athlete.targetRaceDate || this.athlete.target_race_date)
      if (targetRaceDateTimestamp) {
        try {
          const date = new Date(targetRaceDateTimestamp * 1000) // Convert Unix timestamp to milliseconds
          const year = date.getFullYear()
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const day = String(date.getDate()).padStart(2, '0')
          this.targetRaceDate = `${year}-${month}-${day}`
        } catch (e) {
          console.error('Error parsing target race date:', e)
          this.targetRaceDate = ''
        }
      } else {
        this.targetRaceDate = ''
      }

      // Populate target sport types (support both camelCase and snake_case from backend)
      const targetSportTypes = this.athlete && (this.athlete.targetSportTypes || this.athlete.target_sport_types)
      if (Array.isArray(targetSportTypes) && targetSportTypes.length > 0) {
        this.targetSportTypes = [...targetSportTypes]
      } else {
        this.targetSportTypes = []
      }

      // Initialize goal description
      const goalDescription = this.athlete && (this.athlete.goalDescription || this.athlete.goal_description)
      if (goalDescription) {
        this.goalDescription = goalDescription
      } else {
        this.goalDescription = ''
      }

      console.log('📝 Form fields populated:', {
        email: this.email,
        age: this.age,
        weight: this.weight,
        height: this.height,
        sex: this.sex,
        autoAnalyseAndShare: this.autoAnalyseAndShare,
        targetFinishTime: `${this.targetFinishTimeHours}:${this.targetFinishTimeMinutes}`,
        targetRaceDate: this.targetRaceDate
      })

      // Clear status messages
      this.profileUpdateError = null
      this.profileUpdateSuccess = null
      this.profileUpdateLoading = false
    },

    openEditModal () {
      this.$bvModal.show('edit-profile-modal')
    },

    handleOk (bvModalEvt) {
      // Prevent modal from closing automatically
      bvModalEvt.preventDefault()
      // Trigger form submission
      this.handleSubmit()
    },

    handleSubmit () {
      // Clear previous messages
      this.profileUpdateError = null
      this.profileUpdateSuccess = null

      // Validate form
      if (!this.checkFormValidity()) {
        // Show validation error inline
        this.profileUpdateError = this.$t('profile.pleaseFixValidationErrors')
        return
      }

      // Check if athlete data is available
      if (!this.athlete || !this.athlete._id) {
        console.error('No athlete data available:', this.athlete)

        // Try to reload athlete from localStorage
        const athleteData = localStorage.getItem('athlete')
        if (athleteData && athleteData !== 'undefined') {
          try {
            this.athlete = JSON.parse(athleteData)
            console.log('Reloaded athlete from localStorage:', this.athlete)
          } catch (e) {
            console.error('Failed to parse athlete data:', e)
            // Redirect to Strava login
            this.redirectToStravaLogin()
            return
          }
        } else {
          // No athlete data, redirect to Strava login
          this.redirectToStravaLogin()
          return
        }

        // Check again after reload
        if (!this.athlete || !this.athlete._id) {
          // Still no athlete data, redirect to Strava login
          this.redirectToStravaLogin()
          return
        }
      }

      // Set loading state
      this.profileUpdateLoading = true

      // Update athlete data with form values
      this.athlete.email = this.email
      this.athlete.age = this.age ? parseInt(this.age) : null
      this.athlete.weight = this.weight ? parseFloat(this.weight) : null
      this.athlete.height = this.height ? parseFloat(this.height) : null

      // Map form sex values (male/female/"") back to DB values (M/F/"")
      if (this.sex === 'male') {
        this.athlete.sex = 'M'
      } else if (this.sex === 'female') {
        this.athlete.sex = 'F'
      } else {
        this.athlete.sex = '' // Empty string = unknown
      }

      this.athlete.autoAnalyseAndShare = this.autoAnalyseAndShare || false

      // Convert target finish time to total minutes for storage
      if (this.targetFinishTimeHours || this.targetFinishTimeMinutes) {
        const hours = parseInt(this.targetFinishTimeHours) || 0
        const minutes = parseInt(this.targetFinishTimeMinutes) || 0
        this.athlete.targetFinishTime = (hours * 60) + minutes
      } else {
        this.athlete.targetFinishTime = null
      }

      // Convert target race date to Unix timestamp for storage
      if (this.targetRaceDate) {
        try {
          const date = new Date(this.targetRaceDate)
          // Convert to Unix timestamp (seconds since epoch)
          this.athlete.targetRaceDate = Math.floor(date.getTime() / 1000)
        } catch (e) {
          console.error('Error converting target race date to timestamp:', e)
          this.athlete.targetRaceDate = null
        }
      } else {
        this.athlete.targetRaceDate = null
      }

      // Store target sport types as array
      if (this.targetSportTypes && this.targetSportTypes.length > 0) {
        this.athlete.targetSportTypes = [...this.targetSportTypes]
      } else {
        this.athlete.targetSportTypes = null
      }

      // Store goal description
      if (this.goalDescription && this.goalDescription.trim().length > 0) {
        this.athlete.goal_description = this.goalDescription.trim()
      } else {
        this.athlete.goal_description = null
      }

      console.log('💾 Submitting athlete data to backend:', {
        email: this.athlete.email,
        age: this.athlete.age,
        weight: this.athlete.weight,
        height: this.athlete.height,
        sex: this.athlete.sex,
        autoAnalyseAndShare: this.athlete.autoAnalyseAndShare,
        targetFinishTime: this.athlete.targetFinishTime,
        targetRaceDate: this.athlete.targetRaceDate
      })

      // Submit to backend
      $backend.updateProfile(this.athlete).then(responseData => {
        // Update local storage and component state
        localStorage.setItem('athlete', JSON.stringify(responseData.data))
        this.athlete = responseData.data

        // Show success message inline
        this.profileUpdateSuccess = this.$t('profile.updateSuccess')
        this.profileUpdateLoading = false

        // Close modal after a short delay to show success message
        setTimeout(() => {
          this.$bvModal.hide('edit-profile-modal')
        }, 1500)
      }).catch(error => {
        // Handle error
        const errorMsg = (error && error.data && error.data.msg) || this.$t('profile.updateFailed')

        // Show error inline
        this.profileUpdateError = errorMsg
        this.profileUpdateLoading = false

        // Handle unauthorized error
        if (errorMsg === 'unauthorized') {
          this.handleUnauthorizedError()
        }
      })
    },

    formatEventDate (timestamp) {
      return new Date(timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    isUpcomingEvent (timestamp) {
      return new Date(timestamp) > new Date()
    },

    isExpiredEvent (timestamp) {
      return new Date(timestamp) < new Date()
    },

    showAchievement (event) {
      if (!this.athlete || !this.athlete._id) {
        this.$bvToast.toast(this.$t('profile.athleteNotFound'), {
          title: this.$t('profile.error'),
          variant: 'danger',
          solid: true
        })
        return
      }

      this.selectedEvent = event
      this.achievementModalVisible = true
      this.achievementLoading = true
      this.achievementError = ''
      this.achievementData = null
      $backend.getAthleteStats(this.athlete._id, event.slug)
        .then(response => {
          this.achievementData = response.data
          this.achievementLoading = false
        })
        .catch(error => {
          this.achievementError = this.getErrorMessage(error)
          this.achievementLoading = false

          // Handle unauthorized error
          if (error.data && error.data.msg === 'unauthorized') {
            this.handleUnauthorizedError()
          }
        })
    },

    formatDate (timestamp) {
      return new Date(timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    formatPace (activity) {
      if (!activity.moving_time || !activity.km) return '-'
      const paceSeconds = activity.moving_time / activity.km
      const minutes = Math.floor(paceSeconds / 60)
      const seconds = Math.floor(paceSeconds % 60)
      return `${minutes}:${seconds.toString().padStart(2, '0')}/km`
    },

    formatMovingTime (seconds) {
      if (!seconds) return '-'
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const remainingSeconds = seconds % 60

      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
      } else {
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
      }
    },

    stats (activities) {
      if (this.showStatsSummary) {
        this.showStatsSummary = false
        return
      }
      let dict = {}
      this.selectedEvent.type.map((t) => {
        dict[t] = { block: 0, km: 0, point: 0, count: 0 }
      })

      activities.map((act) => {
        dict[act.type].block += act.block
        dict[act.type].km += act.km
        dict[act.type].point += act.point
        dict[act.type].count += 1
      })
      this.athleteStats = dict
      this.showStatsSummary = true
    },

    openAvatarModal () {
      if (!this.athlete || !this.athlete._id) {
        this.$bvToast.toast(this.$t('profile.athleteNotFound'), {
          title: this.$t('profile.error'),
          variant: 'danger',
          solid: true
        })
        return
      }

      this.avatarSelectedFile = null
      this.avatarUploadError = ''
      this.$bvModal.show('avatar-upload-modal')
    },

    handleAvatarFileSelected (file) {
      this.avatarSelectedFile = file
      this.avatarUploadError = ''
    },

    handleAvatarFileCleared () {
      this.avatarSelectedFile = null
      this.avatarUploadError = ''
    },

    handleAvatarError (error) {
      this.avatarUploadError = error
    },

    handleAvatarUpload () {
      if (!this.avatarSelectedFile) {
        this.$bvToast.toast(this.$t('profile.noFileSelected'), {
          title: this.$t('profile.error'),
          variant: 'danger',
          solid: true
        })
        return
      }

      if (!this.athlete || !this.athlete._id) {
        this.$bvToast.toast(this.$t('profile.athleteNotFound'), {
          title: this.$t('profile.error'),
          variant: 'danger',
          solid: true
        })
        return
      }

      this.avatarUploadLoading = true
      this.avatarUploadError = ''

      const formData = new FormData()
      formData.append('file', this.avatarSelectedFile)

      $backend.uploadAvatar(this.athlete._id, formData, this.athlete).then(response => {
        this.athlete.profile = response.data.avatar_url
        // Update local storage with new avatar URL
        localStorage.setItem('athlete', JSON.stringify(this.athlete))
        this.$bvModal.hide('avatar-upload-modal')
        this.$bvToast.toast(this.$t('profile.avatarUploadSuccess'), {
          title: this.$t('profile.success'),
          variant: 'success',
          solid: true
        })
      }).catch(error => {
        this.avatarUploadError = this.getErrorMessage(error)

        // Handle unauthorized error
        if (error.data && error.data.msg === 'unauthorized') {
          this.handleUnauthorizedError()
        }
      }).finally(() => {
        this.avatarUploadLoading = false
      })
    },

    resetAvatarModal () {
      this.avatarSelectedFile = null
      this.avatarUploadError = ''
      if (this.$refs.avatarUploader) {
        this.$refs.avatarUploader.reset()
      }
    },

    handleAiGen (event) {
      if (!this.athlete || !this.athlete._id) {
        this.$bvToast.toast(this.$t('profile.athleteNotFound'), {
          title: this.$t('profile.error'),
          variant: 'danger',
          solid: true
        })
        return
      }

      this.selectedEvent = event
      this.aiGenModalVisible = true
      this.aiGenLoading = true
      this.aiGenError = ''
      this.tracklogData = null

      // Call real API endpoint to get athlete stats
      $backend.getAthleteStats(this.athlete._id, event.slug)
        .then(response => {
          this.tracklogData = response.data.activities
          this.aiGenLoading = false
        })
        .catch(error => {
          this.aiGenError = this.getErrorMessage(error)
          this.aiGenLoading = false

          // Handle unauthorized error
          if (error.data && error.data.msg === 'unauthorized') {
            this.handleUnauthorizedError()
          }
        })
    },

    generateAiContent (tracklog, type = 'image') {
      // Check if athlete is authenticated
      if (!this.athlete || !this.athlete.token || !this.athlete.token.access_token) {
        this.$bvToast.toast(this.$t('profile.athleteNotFound'), {
          title: 'AI Gen Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        })
        return
      }

      // Check if tracklog has an ID
      if (!tracklog.id) {
        this.$bvToast.toast('Activity ID not found', {
          title: 'AI Gen Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        })
        return
      }

      // Set up the modal based on type
      this.selectedTracklogName = tracklog.name || tracklog.type
      this.currentGenerationType = type
      this.currentActivityId = tracklog.id
      this.currentContentPrivacy = tracklog[`ai_${type}_private`] || false
      this.aiImageLoading = true
      this.aiImageError = ''
      this.generatedImageUrl = ''
      this.generatedVideoUrl = ''
      this.aiImageModalVisible = true

      // Call the AI generation API with type
      $backend.generateAiContent(this.athlete._id, tracklog.id, this.athlete, type)
        .then(response => {
          if (type === 'image' && response.image_url) {
            // Success - show the generated image in modal
            this.generatedImageUrl = response.image_url
            this.generatedVideoUrl = ''
            this.aiImageLoading = false

            this.$bvToast.toast('AI image generated successfully!', {
              title: 'AI Gen Success',
              variant: 'success',
              solid: true,
              autoHideDelay: 3000
            })
          } else if (type === 'video' && response.video_url) {
            // Success - show the generated video
            this.generatedImageUrl = ''
            this.generatedVideoUrl = response.video_url
            this.aiImageLoading = false

            this.$bvToast.toast('AI video generated successfully!', {
              title: 'AI Gen Success',
              variant: 'success',
              solid: true,
              autoHideDelay: 3000
            })
          } else {
            throw new Error(`No ${type} URL received`)
          }
        })
        .catch(error => {
          console.error('AI generation error:', error)
          console.error('Error data:', error.data)

          this.aiImageLoading = false

          // Use the helper method to get properly translated error message
          const errorMessage = this.getErrorMessage(error)

          // Handle unauthorized error
          if (error.data && error.data.msg === 'unauthorized') {
            this.handleUnauthorizedError()
            return
          }

          this.aiImageError = errorMessage

          this.$bvToast.toast(errorMessage, {
            title: 'AI Gen Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          })
        })
    },

    formatDateTime (timestamp) {
      return new Date(timestamp).toISOString().split('T')[0]
    },

    onImageLoad () {
    },

    onImageError () {
      this.aiImageError = 'Failed to load the generated image'
      this.$bvToast.toast('Failed to load the generated image', {
        title: 'Image Load Error',
        variant: 'danger',
        solid: true,
        autoHideDelay: 5000
      })
    },

    onVideoLoad () {
    },

    onVideoError () {
      this.aiImageError = 'Failed to load the generated video'
      this.$bvToast.toast('Failed to load the generated video', {
        title: 'Video Load Error',
        variant: 'danger',
        solid: true,
        autoHideDelay: 5000
      })
    },

    downloadImage () {
      if (this.generatedImageUrl) {
        const link = document.createElement('a')
        link.href = this.generatedImageUrl
        link.download = `ai-generated-${this.selectedTracklogName.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}.jpg`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },

    openImageInNewTab () {
      if (this.generatedImageUrl) {
        window.open(this.generatedImageUrl, '_blank')
      }
    },

    shareImage () {
      if (this.generatedImageUrl && navigator.share) {
        navigator.share({
          title: `AI Generated Image for ${this.selectedTracklogName}`,
          text: `Check out this AI-generated image for my ${this.selectedTracklogName} activity!`,
          url: this.generatedImageUrl
        }).catch(() => {
          // Fallback to copying URL to clipboard
          this.copyImageUrl()
        })
      } else {
        // Fallback to copying URL to clipboard
        this.copyImageUrl()
      }
    },

    copyImageUrl () {
      if (this.generatedImageUrl) {
        navigator.clipboard.writeText(this.generatedImageUrl).then(() => {
          this.$bvToast.toast('Image URL copied to clipboard!', {
            title: 'Copied',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000
          })
        }).catch(() => {
          this.$bvToast.toast('Failed to copy URL to clipboard', {
            title: 'Copy Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 3000
          })
        })
      }
    },

    downloadVideo () {
      if (this.generatedVideoUrl) {
        const link = document.createElement('a')
        link.href = this.generatedVideoUrl
        link.download = `ai-generated-video-${this.selectedTracklogName.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}.mp4`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },

    openContentInNewTab () {
      const url = this.currentGenerationType === 'video' ? this.generatedVideoUrl : this.generatedImageUrl
      if (url) {
        window.open(url, '_blank')
      }
    },

    shareContent () {
      const url = this.currentGenerationType === 'video' ? this.generatedVideoUrl : this.generatedImageUrl
      const contentType = this.currentGenerationType === 'video' ? 'video' : 'image'

      if (url && navigator.share) {
        navigator.share({
          title: `AI Generated ${contentType} for ${this.selectedTracklogName}`,
          text: `Check out this AI-generated ${contentType} for my ${this.selectedTracklogName} activity!`,
          url: url
        }).catch(() => {
          // Fallback to copying URL to clipboard
          this.copyContentUrl()
        })
      } else {
        // Fallback to copying URL to clipboard
        this.copyContentUrl()
      }
    },

    copyContentUrl () {
      const url = this.currentGenerationType === 'video' ? this.generatedVideoUrl : this.generatedImageUrl
      const contentType = this.currentGenerationType === 'video' ? 'Video' : 'Image'

      if (url) {
        navigator.clipboard.writeText(url).then(() => {
          this.$bvToast.toast(`${contentType} URL copied to clipboard!`, {
            title: 'Copied',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000
          })
        }).catch(() => {
          this.$bvToast.toast('Failed to copy URL to clipboard', {
            title: 'Copy Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 3000
          })
        })
      }
    },

    copyContentText () {
      if (!this.selectedContentText) {
        this.$bvToast.toast('No content to copy', {
          title: 'Copy Error',
          variant: 'warning',
          solid: true,
          autoHideDelay: 3000
        })
        return
      }

      // Strip HTML tags to get plain text for copying
      const plainText = this.selectedContentText
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
        .replace(/&amp;/g, '&') // Replace HTML entities
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .trim()

      // Try modern clipboard API first (works on desktop and mobile browsers)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(plainText).then(() => {
          this.$bvToast.toast('Content copied to clipboard!', {
            title: 'Copied',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000
          })
        }).catch(() => {
          // Fallback to older method
          this.fallbackCopyTextToClipboard(plainText)
        })
      } else {
        // Fallback for older browsers
        this.fallbackCopyTextToClipboard(plainText)
      }
    },

    fallbackCopyTextToClipboard (text) {
      // Create a temporary textarea element
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)

      try {
        textArea.focus()
        textArea.select()
        const successful = document.execCommand('copy')

        if (successful) {
          this.$bvToast.toast('Content copied to clipboard!', {
            title: 'Copied',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000
          })
        } else {
          throw new Error('Copy command failed')
        }
      } catch (err) {
        this.$bvToast.toast('Failed to copy content to clipboard', {
          title: 'Copy Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000
        })
      } finally {
        document.body.removeChild(textArea)
      }
    },

    copyAnalysisText () {
      if (!this.aiAnalysisData || !this.aiAnalysisData.analysis) {
        this.$bvToast.toast('No analysis content to copy', {
          title: 'Copy Error',
          variant: 'warning',
          solid: true,
          autoHideDelay: 3000
        })
        return
      }

      // Strip HTML tags to get plain text for copying
      const plainText = this.aiAnalysisData.analysis
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
        .replace(/&amp;/g, '&') // Replace HTML entities
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .trim()

      // Try modern clipboard API first (works on desktop and mobile browsers)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(plainText).then(() => {
          this.$bvToast.toast('Analysis copied to clipboard!', {
            title: 'Copied',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000
          })
        }).catch(() => {
          // Fallback to older method
          this.fallbackCopyTextToClipboard(plainText)
        })
      } else {
        // Fallback for older browsers
        this.fallbackCopyTextToClipboard(plainText)
      }
    },

    checkPendingStravaShare () {
      // Check if there's a pending share after OAuth callback
      const pendingShare = localStorage.getItem('pendingStravaShare')

      if (pendingShare) {
        try {
          const payload = JSON.parse(pendingShare)

          // Check if the pending share is not too old (within 10 minutes)
          const timeDiff = Date.now() - payload.timestamp
          if (timeDiff < 10 * 60 * 1000) {
            // Remove from localStorage
            localStorage.removeItem('pendingStravaShare')

            // Retry the share
            this.retryStravaShare(payload)
          } else {
            // Too old, remove it
            localStorage.removeItem('pendingStravaShare')
          }
        } catch (error) {
          console.error('Error processing pending share:', error)
          localStorage.removeItem('pendingStravaShare')
        }
      }
    },

    async retryStravaShare (payload) {
      // Check if athlete exists
      if (!this.athlete || !this.athlete.token || !this.athlete.token.access_token) {
        this.shareToStravaError = 'You need to be logged in to share to Strava'
        this.$bvModal.show('ai-analysis-modal')
        return
      }

      // Clear previous messages
      this.shareToStravaError = null
      this.shareToStravaSuccess = null
      this.sharedStravaUrl = null
      this.shareToStravaLoading = true

      try {
        // Call backend API
        const response = await $backend.shareToSocial('strava', payload, this.athlete)

        if (response && response.msg) {
          this.shareToStravaSuccess = response.msg

          // Store the Strava URL if available
          if (response.strava_url) {
            this.sharedStravaUrl = response.strava_url
          }

          // Open the AI analysis modal to show success message
          this.$bvModal.show('ai-analysis-modal')
        }
      } catch (error) {
        let errorMsg = 'Failed to share to Strava'

        if (error && error.data && error.data.msg) {
          errorMsg = error.data.msg
        }

        this.shareToStravaError = errorMsg

        // Open the AI analysis modal to show error message
        this.$bvModal.show('ai-analysis-modal')
      } finally {
        this.shareToStravaLoading = false
      }
    },

    async shareContentToStrava () {
      if (!this.selectedContentText || !this.selectedContentActivityId) {
        this.$bvToast.toast('No content or activity ID to share', {
          title: 'Share Error',
          variant: 'warning',
          solid: true,
          autoHideDelay: 3000
        })
        return
      }

      if (!this.athlete || !this.athlete.token || !this.athlete.token.access_token) {
        this.$bvToast.toast('You need to be logged in to share to Strava', {
          title: 'Share Error',
          variant: 'warning',
          solid: true,
          autoHideDelay: 3000
        })
        return
      }

      // Clear previous messages
      this.shareToStravaError = null
      this.shareToStravaSuccess = null
      this.sharedStravaUrl = null
      this.shareToStravaLoading = true

      // Prepare payload
      const payload = {
        activityId: this.selectedContentActivityId,
        analysis: this.selectedContentText,
        timestamp: Date.now()
      }

      try {
        // Call backend API
        const response = await $backend.shareToSocial('strava', payload, this.athlete)

        if (response && response.msg) {
          // Store the Strava URL if available
          if (response.strava_url) {
            this.sharedStravaUrl = response.strava_url
          }

          this.$bvToast.toast(response.msg, {
            title: 'Success',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000
          })
        }
      } catch (error) {
        if (error && error.status === 401) {
          // User doesn't have write permission
          const errorMsg = (error.data && error.data.msg) || 'user not grant write permission'

          if (errorMsg.includes('not grant write permission')) {
            // Store pending share data
            localStorage.setItem('pendingStravaShare', JSON.stringify(payload))

            // Store current profile page URL for redirect after OAuth
            const currentUrl = window.location.pathname + window.location.search + window.location.hash
            localStorage.setItem('redirectUrl', currentUrl)

            // Show message
            this.$bvToast.toast('You need to grant write permission to share to Strava. Redirecting to authorization...', {
              title: 'Authorization Required',
              variant: 'warning',
              solid: true,
              autoHideDelay: 3000
            })

            // Construct Strava OAuth URL with activity:write scope
            const authUrl = config.getStravaAuthUrl('write')

            // Redirect to Strava OAuth after delay
            setTimeout(() => {
              window.location.href = authUrl
            }, 2000)
          }
        } else if (error && error.status === 400) {
          // Bad request
          const errorMsg = (error.data && error.data.msg) || 'Failed to share to Strava'
          this.$bvToast.toast(errorMsg, {
            title: 'Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          })
        } else {
          // Other errors
          const errorMsg = (error && error.data && error.data.msg) || 'An error occurred while sharing to Strava'
          this.$bvToast.toast(errorMsg, {
            title: 'Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          })
        }
      } finally {
        this.shareToStravaLoading = false
      }
    },

    async shareToStrava () {
      if (!this.aiAnalysisData || !this.aiAnalysisData.analysis) {
        this.shareToStravaError = 'No analysis content to share'
        return
      }

      if (!this.athlete || !this.athlete.token || !this.athlete.token.access_token) {
        this.shareToStravaError = 'You need to be logged in to share to Strava'
        return
      }

      // Clear previous messages
      this.shareToStravaError = null
      this.shareToStravaSuccess = null
      this.sharedStravaUrl = null
      this.shareToStravaLoading = true

      // Prepare payload
      const payload = {
        activityId: this.aiAnalysisData.current_activity.activity_id,
        analysis: this.aiAnalysisData.analysis,
        timestamp: Date.now()
      }

      try {
        // Call backend API
        const response = await $backend.shareToSocial('strava', payload, this.athlete)

        if (response && response.msg) {
          this.shareToStravaSuccess = response.msg

          // Store the Strava URL if available
          if (response.strava_url) {
            this.sharedStravaUrl = response.strava_url
          }
        }
      } catch (error) {
        if (error && error.status === 401) {
          // User doesn't have write permission
          const errorMsg = (error.data && error.data.msg) || 'user not grant write permission'

          if (errorMsg.includes('not grant write permission')) {
            // Store pending share data
            localStorage.setItem('pendingStravaShare', JSON.stringify(payload))

            // Store current profile page URL for redirect after OAuth
            const currentUrl = window.location.pathname + window.location.search + window.location.hash
            localStorage.setItem('redirectUrl', currentUrl)

            // Show message
            this.shareToStravaError = 'You need to grant write permission to share to Strava. Redirecting to authorization...'

            // Construct Strava OAuth URL with activity:write scope
            const authUrl = config.getStravaAuthUrl('write')

            // Redirect to Strava OAuth after delay
            setTimeout(() => {
              window.location.href = authUrl
            }, 2000)
          }
        } else if (error && error.status === 400) {
          // Bad request
          const errorMsg = (error.data && error.data.msg) || 'Failed to share to Strava'
          this.shareToStravaError = errorMsg
        } else {
          // Other errors
          const errorMsg = (error && error.data && error.data.msg) || 'An error occurred while sharing to Strava'
          this.shareToStravaError = errorMsg
        }
      } finally {
        this.shareToStravaLoading = false
      }
    },

    showVideoUploadModal (tracklog) {
      // Store the tracklog for later use
      this.pendingVideoGeneration = tracklog
      this.sourceSelectedFile = null
      this.sourceUploadError = ''
      this.aiSourceUploadModalVisible = true
    },

    handleSourceFileSelected (file) {
      this.sourceSelectedFile = file
      this.sourceUploadError = ''
    },

    handleSourceFileCleared () {
      this.sourceSelectedFile = null
      this.sourceUploadError = ''
    },

    handleSourceError (error) {
      this.sourceUploadError = error
    },

    async handleSourceUpload () {
      if (!this.sourceSelectedFile || !this.pendingVideoGeneration) {
        return
      }

      this.sourceUploadLoading = true
      this.sourceUploadError = ''

      try {
        const formData = new FormData()
        formData.append('file', this.sourceSelectedFile)

        const response = await $backend.uploadSourceImage(
          this.athlete._id,
          this.pendingVideoGeneration.id,
          formData,
          this.athlete
        )

        if (response && response.data && response.data.source_url) {
          // Close upload modal
          this.aiSourceUploadModalVisible = false

          // Start video generation with the uploaded source image
          await this.generateAiContentWithSource(this.pendingVideoGeneration, 'video', response.data.source_url)
        } else {
          console.error('Video upload failed: No source URL in response', response)
          throw new Error('No source URL received from upload')
        }
      } catch (error) {
        console.error('Source upload error:', error)
        const resp = error && error.response ? error.response : error
        let msg = this.$t('profile.uploadFailed')
        if (resp && resp.data) {
          if (typeof resp.data === 'string') {
            msg = resp.data
          } else if (resp.data.msg) {
            msg = resp.data.msg
          } else if (resp.data.message) {
            msg = resp.data.message
          }
        }
        this.sourceUploadError = msg
      } finally {
        this.sourceUploadLoading = false
      }
    },

    cancelSourceUpload () {
      this.aiSourceUploadModalVisible = false
      this.sourceSelectedFile = null
      this.sourceUploadError = ''
      this.pendingVideoGeneration = null
      if (this.$refs.sourceUploader) {
        this.$refs.sourceUploader.reset()
      }
    },

    async generateAiContentWithSource (tracklog, type, sourceImageUrl) {
      // Check if athlete is authenticated
      if (!this.athlete || !this.athlete.token || !this.athlete.token.access_token) {
        console.error('Authentication failed:', {
          hasAthlete: !!this.athlete,
          hasToken: !!(this.athlete && this.athlete.token),
          hasAccessToken: !!(this.athlete && this.athlete.token && this.athlete.token.access_token)
        })
        this.$bvToast.toast(this.$t('profile.athleteNotFound'), {
          title: 'AI Gen Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        })
        return
      }

      // Check if tracklog has an ID
      if (!tracklog.id) {
        console.error('Tracklog ID missing:', tracklog)
        this.$bvToast.toast('Activity ID not found', {
          title: 'AI Gen Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        })
        return
      }

      // Set up the modal based on type
      this.selectedTracklogName = tracklog.name || tracklog.type
      this.currentGenerationType = type
      this.currentActivityId = tracklog.id
      this.currentContentPrivacy = tracklog[`ai_${type}_private`] || false
      this.aiImageLoading = true
      this.aiImageError = ''
      this.generatedImageUrl = ''
      this.generatedVideoUrl = ''
      this.aiImageModalVisible = true

      try {
        // Call the AI generation API with type and source image
        const response = await $backend.generateAiContentWithSource(
          this.athlete._id,
          tracklog.id,
          this.athlete,
          type,
          sourceImageUrl
        )

        if (type === 'image' && response.image_url) {
          // Success - show the generated image
          this.generatedImageUrl = response.image_url
          this.generatedVideoUrl = ''
          this.aiImageLoading = false

          this.$bvToast.toast('AI image generated successfully!', {
            title: 'AI Gen Success',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000
          })
        } else if (type === 'video' && response.video_url) {
          // Success - show the generated video
          this.generatedImageUrl = ''
          this.generatedVideoUrl = response.video_url
          this.aiImageLoading = false

          this.$bvToast.toast('AI video generated successfully!', {
            title: 'AI Gen Success',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000
          })
        } else {
          console.error(`No ${type} URL received in response:`, response)
          throw new Error(`No ${type} URL received`)
        }
      } catch (error) {
        console.error('AI generation error:', error)
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          response: error.response,
          request: error.request,
          config: error.config
        })
        this.aiImageLoading = false
        let errorMessage = `Failed to generate AI ${type}`
        const resp = error && error.response ? error.response : error
        if (resp && resp.data) {
          // Use the helper method to get properly translated error message
          errorMessage = this.getErrorMessage(error)
        }

        // Handle unauthorized error
        if (errorMessage === 'unauthorized') {
          this.handleUnauthorizedError()
          return
        }

        this.aiImageError = errorMessage
        this.$bvToast.toast(this.aiImageError, {
          title: 'AI Gen Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        })
      }
    },

    async toggleContentPrivacy () {
      if (!this.currentActivityId || !this.currentGenerationType) {
        this.$bvToast.toast('Activity information is missing', {
          title: 'Privacy Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000
        })
        return
      }

      try {
        const newPrivacySetting = !this.currentContentPrivacy

        // Call backend API to update privacy setting
        const response = await $backend.updateAiContentPrivacy(
          this.athlete._id,
          this.currentActivityId,
          this.currentGenerationType,
          newPrivacySetting,
          this.athlete
        )

        if (response && response.success) {
          this.currentContentPrivacy = newPrivacySetting
          this.$bvToast.toast(this.$t('profile.privacyUpdated'), {
            title: 'Success',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000
          })
        } else {
          throw new Error('Privacy update failed')
        }
      } catch (error) {
        console.error('Privacy update error:', error)
        this.$bvToast.toast(this.$t('profile.privacyUpdateFailed'), {
          title: 'Privacy Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        })
      }
    },

    // New methods for AI Image generation with image upload
    showAiImageUploadModal (tracklog) {
      // Store the tracklog for later use
      this.pendingImageGeneration = tracklog
      this.aiImageSelectedFile = null
      this.aiImageUploadError = ''
      this.aiImageUploadModalVisible = true

      // Debug logging for mobile
    },

    handleAiImageFileSelected (file) {
      this.aiImageSelectedFile = file
      this.aiImageUploadError = ''
    },

    handleAiImageFileCleared () {
      this.aiImageSelectedFile = null
      this.aiImageUploadError = ''
    },

    handleAiImageError (error) {
      this.aiImageUploadError = error
    },

    async handleAiImageUpload () {
      if (!this.aiImageSelectedFile || !this.pendingImageGeneration) {
        return
      }

      this.aiImageUploadLoading = true
      this.aiImageUploadError = ''

      try {
        const formData = new FormData()
        formData.append('file', this.aiImageSelectedFile)

        // First upload the source image
        const uploadResponse = await $backend.uploadSourceImage(
          this.athlete._id,
          this.pendingImageGeneration.id,
          formData,
          this.athlete
        )

        if (uploadResponse && uploadResponse.data && uploadResponse.data.source_url) {
          // Close upload modal
          this.aiImageUploadModalVisible = false

          // Start image generation with the uploaded source image
          await this.generateAiContentWithSource(this.pendingImageGeneration, 'image', uploadResponse.data.source_url)
        } else {
          console.error('Upload failed: No source URL in response', uploadResponse)
          throw new Error('No source URL received from upload')
        }
      } catch (error) {
        console.error('AI Image upload error:', error)
        const resp = error && error.response ? error.response : error
        let msg = this.$t('profile.uploadFailed')
        if (resp && resp.data) {
          if (typeof resp.data === 'string') {
            msg = resp.data
          } else if (resp.data.msg) {
            msg = resp.data.msg
          } else if (resp.data.message) {
            msg = resp.data.message
          }
        }
        this.aiImageUploadError = msg
      } finally {
        this.aiImageUploadLoading = false
      }
    },

    cancelAiImageUpload () {
      this.aiImageUploadModalVisible = false
      this.aiImageSelectedFile = null
      this.aiImageUploadError = ''
      this.pendingImageGeneration = null
      if (this.$refs.aiImageUploader) {
        this.$refs.aiImageUploader.reset()
      }
    },

    // Tab switching methods
    switchToMyActivitiesTab () {
      this.activeTab = 'my-activities'
      // Load activities if not already loaded
      if (this.activitiesData.length === 0 && !this.activitiesLoading) {
        this.loadActivities()
      }
    },

    switchToChallengesTab () {
      this.activeTab = 'challenges'
      // Load challenges if not already loaded
      if (this.events.length === 0 && !this.loadingMore) {
        this.loadChallenge()
      }
    },

    switchToAiHistoryTab () {
      this.activeTab = 'ai-history'
      // Load AI history if not already loaded
      if (!this.aiHistoryData && !this.aiHistoryLoading) {
        this.loadAiHistory()
      } else if (this.aiHistoryData && this.aiHistoryData.data.length > 0 && !this.isPolling) {
        // Auto-start polling if there's data and polling is not already active
        this.startPolling()
      }
    },

    // AI History Methods
    loadAiHistory (page = 1) {
      if (!this.athlete || !this.athlete._id) {
        this.aiHistoryError = this.$t('profile.athleteNotFound')
        return
      }

      this.aiHistoryLoading = true
      this.aiHistoryError = ''

      const limit = this.aiHistoryPagination.limit
      const offset = (page - 1) * limit

      $backend.getAiHistory(this.athlete._id, limit, offset)
        .then(response => {
          if (response && response.data) {
            this.aiHistoryData = response
            this.aiHistoryPagination.currentPage = page
            if (response.pagination) {
              this.aiHistoryPagination.totalCount = response.pagination.total_count
            }
          } else {
            console.error('Invalid response structure:', response)
            this.aiHistoryError = 'Invalid response from server'
          }
        })
        .catch(error => {
          console.error('Error loading AI history:', error)
          if (error.response && error.response.status === 401) {
            this.handleUnauthorizedError()
          } else {
            this.aiHistoryError = this.getErrorMessage(error.response) || this.$t('profile.errorLoadingAiHistory')
          }
        })
        .finally(() => {
          this.aiHistoryLoading = false
        })
    },

    // Refresh AI History (manual refresh)
    refreshAiHistory () {
      if (this.aiHistoryData && this.aiHistoryData.pagination) {
        this.loadAiHistory(this.aiHistoryData.pagination.current_page)
      } else {
        this.loadAiHistory(1)
      }
    },

    // Polling methods
    startPolling () {
      if (this.isPolling) return

      this.isPolling = true
      this.pollingError = null
      this.lastPollingTime = new Date()

      // Start polling immediately
      this.pollAiHistory()

      // Set up interval for continuous polling
      this.pollingInterval = setInterval(() => {
        if (this.activeTab === 'ai-history' && this.isPolling) {
          this.pollAiHistory()
        }
      }, this.pollingIntervalMs)
    },

    stopPolling () {
      this.isPolling = false
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },

    togglePolling () {
      if (this.isPolling) {
        this.stopPolling()
      } else {
        this.startPolling()
      }
    },

    // Poll AI History (silent refresh)
    pollAiHistory () {
      if (!this.athlete || !this.athlete._id || !this.aiHistoryData) return

      const currentPage = this.aiHistoryData.pagination ? this.aiHistoryData.pagination.current_page : 1
      const limit = this.aiHistoryPagination.limit
      const offset = (currentPage - 1) * limit

      $backend.getAiHistory(this.athlete._id, limit, offset)
        .then(response => {
          if (response && response.data) {
            // Check if there are any status changes
            const hasChanges = this.checkForStatusChanges(response)

            if (hasChanges) {
              this.aiHistoryData = response
              this.lastPollingTime = new Date()

              // Show a subtle notification that data was updated
              this.$bvToast.toast(this.$t('profile.aiHistoryUpdated'), {
                title: this.$t('profile.updateNotification'),
                variant: 'info',
                autoHideDelay: 3000,
                solid: true
              })
            }
          }
        })
        .catch(error => {
          console.warn('Polling error:', error)
          this.pollingError = error.message || 'Polling failed'
          this.pollingErrorCount++

          // Stop polling on persistent errors
          if (this.pollingErrorCount > 3) {
            this.stopPolling()
            this.$bvToast.toast(this.$t('profile.pollingStopped'), {
              title: this.$t('profile.pollingError'),
              variant: 'warning',
              autoHideDelay: 5000
            })
          }
        })
    },

    // Check for status changes in AI history records
    checkForStatusChanges (newData) {
      if (!this.aiHistoryData || !newData || !this.aiHistoryData.data || !newData.data) {
        return false
      }

      const oldRecords = this.aiHistoryData.data
      const newRecords = newData.data

      // Check if any record status has changed
      for (let i = 0; i < Math.min(oldRecords.length, newRecords.length); i++) {
        const oldRecord = oldRecords[i]
        const newRecord = newRecords[i]

        if (oldRecord.activity_id === newRecord.activity_id &&
            oldRecord.content_type === newRecord.content_type &&
            oldRecord.status !== newRecord.status) {
          return true
        }
      }

      // Check if new records were added
      if (newRecords.length > oldRecords.length) {
        return true
      }

      return false
    },

    formatTimestamp (timestamp) {
      if (!timestamp) return this.$t('profile.unknown')
      const date = new Date(timestamp * 1000)
      return date.toLocaleString()
    },

    // Content Preview Methods
    previewContent (contentUrl, contentType) {
      this.previewContentUrl = contentUrl
      this.previewContentType = contentType
      this.previewLoading = true
      this.previewError = false
      this.contentPreviewModalVisible = true
    },

    onPreviewError () {
      this.previewLoading = false
      this.previewError = true
    },

    onPreviewLoad () {
      this.previewLoading = false
      this.previewError = false
    },

    toggleImageZoom () {
      if (this.previewContentType === 'image') {
        this.imageZoomed = !this.imageZoomed
      }
    },

    downloadContent () {
      if (!this.previewContentUrl) return

      const link = document.createElement('a')
      link.href = this.previewContentUrl
      link.download = `ai-generated-${this.previewContentType}-${Date.now()}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    openInNewTab () {
      if (!this.previewContentUrl) return
      window.open(this.previewContentUrl, '_blank')
    },

    // Helper method to handle error messages with translation keys
    getErrorMessage (error) {
      if (!error || !error.data) return this.$t('profile.error')

      // Check if there's a translation key provided
      if (error.data.msg_trans_key) {
        return this.$t('profile.' + error.data.msg_trans_key)
      }

      // Fallback to the msg field
      return error.data.msg || this.$t('profile.error')
    },

    // Toggle privacy setting for AI-generated content
    togglePrivacy (record) {
      const key = record.activity_id + '-' + record.content_type

      // Set loading state
      this.$set(this.privacyLoading, key, true)

      const newPrivacy = !record.is_private

      // Make API call to update privacy
      $backend.setAiContentPrivacy(this.athlete._id, record.activity_id, record.content_type, newPrivacy)
        .then(response => {
          // Update the record's privacy status
          record.is_private = newPrivacy

          // Show success message
          this.$bvToast.toast(
            newPrivacy ? this.$t('profile.contentMadePrivate') : this.$t('profile.contentMadePublic'),
            {
              title: this.$t('profile.success'),
              variant: 'success',
              solid: true,
              autoHideDelay: 3000
            }
          )
        })
        .catch(error => {
          // Show error message
          const errorMessage = this.getErrorMessage(error)
          this.$bvToast.toast(errorMessage, {
            title: this.$t('profile.error'),
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          })
        })
        .finally(() => {
          // Clear loading state
          this.$set(this.privacyLoading, key, false)
        })
    },

    showAiTracklogStatsModal (activity) {
      // Check if athlete is authenticated
      if (!this.athlete || !this.athlete.token || !this.athlete.token.access_token) {
        this.$bvToast.toast(this.$t('profile.athleteNotFound'), {
          title: 'AI Stats Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        })
        return
      }

      // Check if activity has an ID
      if (!activity.id) {
        this.$bvToast.toast('Activity ID not found', {
          title: 'AI Stats Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        })
        return
      }

      // Set loading state and show modal immediately
      this.aiAnalysisLoading = true
      this.aiAnalysisData = null
      this.aiAnalysisError = null
      // Clear share status messages
      this.shareToStravaError = null
      this.shareToStravaSuccess = null
      this.sharedStravaUrl = null
      this.shareToStravaLoading = false
      this.$bvModal.show('ai-analysis-modal')

      // Call the AI tracklog stats API
      $backend.getAiTracklogStats(this.athlete._id, activity.id, this.athlete)
        .then(response => {
          if (response.analysis) {
            // Success - set the AI analysis data
            this.aiAnalysisData = response

            this.$bvToast.toast('AI performance analysis generated successfully!', {
              title: 'AI Analysis Success',
              variant: 'success',
              solid: true,
              autoHideDelay: 3000
            })
          }
        })
        .catch(error => {
          // Handle errors with user-friendly messages
          let errorMessage = this.$t('profile.error')
          let errorVariant = 'danger'
          let autoHideDelay = 5000

          // Check if response has daily_ai_analysis_limit_exceeded translation key
          if (error.data && error.data.msg_trans_key === 'daily_ai_analysis_limit_exceeded') {
            errorMessage = this.$t('profile.dailyAiAnalysisLimitExceeded')
            this.aiAnalysisError = this.$t('profile.dailyAiAnalysisLimitExceeded')
            errorVariant = 'warning'
            autoHideDelay = 8000
          }

          // Handle unauthorized error
          if (error.data && error.data.msg === 'unauthorized') {
            this.handleUnauthorizedError()
            return
          }

          // Also show toast notification
          this.$bvToast.toast(errorMessage, {
            variant: errorVariant,
            solid: true,
            autoHideDelay: autoHideDelay
          })
        })
        .finally(() => {
          // Clear loading state
          this.aiAnalysisLoading = false
        })
    },

    getContentTextPreview (text, maxLength = 150) {
      if (!text) return ''
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },

    showContentText (contentText, contentType, activityId) {
      this.selectedContentText = contentText
      this.selectedContentTitle = ''
      this.selectedContentActivityId = activityId || null
      this.sharedStravaUrl = null
      this.showContentTextModal = true
    },

    getRecordKey (record, index) {
      return `${record.activity_id}-${record.content_type}-${record.created_at}-${index}`
    },

    // My Activities Methods
    loadActivities (page = 1, append = false) {
      if (!this.athlete || !this.athlete._id) {
        this.activitiesError = this.$t('profile.athleteNotFound')
        return
      }

      if (append) {
        this.activitiesLoadingMore = true
      } else {
        this.activitiesLoading = true
        this.activitiesError = null
      }

      const limit = this.activitiesPagination.limit
      const offset = (page - 1) * limit

      $backend.getAthleteActivities(this.athlete._id, this.athlete.token.access_token, offset, limit)
        .then(response => {
          if (response && response.data) {
            const newActivities = response.data
            if (append) {
              this.activitiesData = this.activitiesData.concat(newActivities)
            } else {
              this.activitiesData = newActivities
            }

            this.activitiesPagination.totalCount = response.count
            this.activitiesPagination.currentPage = page
            this.activitiesHasMore = this.activitiesData.length < response.count

            // Show no activities modal if no activities found and not appending (first load)
            const maxMyActivities = 10
            if (!append && response.count < maxMyActivities) {
              this.$nextTick(() => {
                this.displayNeedSyncMore = true
              })
            }

            // Initialize maps for new activities (only for new activities, not when appending)
            if (!append) {
              this.$nextTick(() => {
                this.initializeActivityMaps()
              })
            } else {
              // For appended activities, initialize maps for only the new ones
              this.$nextTick(() => {
                const startIndex = this.activitiesData.length - newActivities.length
                this.initializeNewActivityMaps(newActivities, startIndex)
              })
            }
          } else {
            this.activitiesError = 'Invalid response from server'
          }
        })
        .catch(error => {
          console.error('Error loading activities:', error)
          if (error.response && error.response.status === 401) {
            this.handleUnauthorizedError()
          } else {
            this.activitiesError = this.getErrorMessage(error.response) || this.$t('profile.errorLoadingActivities')
          }
        })
        .finally(() => {
          this.activitiesLoading = false
          this.activitiesLoadingMore = false
        })
    },

    loadMoreActivities () {
      if (this.activitiesLoadingMore || !this.activitiesHasMore) return
      const nextPage = this.activitiesPagination.currentPage + 1
      this.loadActivities(nextPage, true)
    },

    getActivityIcon (activityType) {
      const iconMap = {
        'Run': 'running',
        'VirtualRun': 'running',
        'Ride': 'biking',
        'VirtualRide': 'biking',
        'Swim': 'swimmer',
        'Walk': 'walking',
        'Hike': 'hiking',
        'TrailRun': 'running'
      }
      return iconMap[activityType] || 'running'
    },

    formatActivityDate (dateString) {
      if (!dateString) return 'N/A'
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      } catch (error) {
        return 'N/A'
      }
    },

    formatTime (seconds) {
      if (!seconds || seconds === 0) return 'N/A'
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const secs = seconds % 60

      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      } else {
        return `${minutes}:${secs.toString().padStart(2, '0')}`
      }
    },

    // Map initialization methods with enhanced reliability
    initializeActivityMaps () {
      if (!this.activitiesData || this.activitiesData.length === 0) return

      // Clean up existing maps first
      this.destroyActivityMaps()

      // Use multiple strategies to ensure DOM is ready
      this.$nextTick(() => {
        // Strategy 1: Immediate check
        this.attemptMapInitialization()

        // Strategy 2: Fallback with longer delay
        setTimeout(() => {
          this.attemptMapInitialization()
        }, 300)

        // Strategy 3: Final fallback
        setTimeout(() => {
          this.attemptMapInitialization()
        }, 800)
      })
    },

    attemptMapInitialization () {
      if (!this.activitiesData || this.activitiesData.length === 0) return

      const activitiesWithPolylines = this.activitiesData.filter(activity =>
        activity.summary_polyline && activity.summary_polyline.trim().length > 0
      )

      activitiesWithPolylines.forEach((activity, index) => {
        const originalIndex = this.activitiesData.findIndex(a => a.id === activity.id)
        this.createActivityMap(activity, originalIndex)
      })
    },

    initializeNewActivityMaps (newActivities, startIndex) {
      if (!newActivities || newActivities.length === 0) return

      this.$nextTick(() => {
        // Wait a bit more for DOM to be ready (like EventMap.vue)
        setTimeout(() => {
          newActivities.forEach((activity, relativeIndex) => {
            const absoluteIndex = startIndex + relativeIndex
            if (activity.summary_polyline) {
              this.createActivityMap(activity, absoluteIndex)
            }
          })
        }, 300) // Increased timeout to ensure DOM is ready
      })
    },

    createActivityMap (activity, index) {
      const mapId = `map-${activity.id}-${index}`

      // Try to get the container using getElementById
      let mapContainer = document.getElementById(mapId)

      if (!mapContainer) {
        console.warn(`Map container not found: ${mapId}`)
        return
      }

      if (this.activityMaps[mapId]) {
        return
      }

      // Advanced container validation with multiple retry attempts
      const validateAndInitializeMap = (retryCount = 0) => {
        const maxRetries = 5
        const retryDelay = 200

        // Check if container is visible and has proper dimensions
        const isVisible = mapContainer.offsetParent !== null
        const hasDimensions = mapContainer.offsetWidth > 0 && mapContainer.offsetHeight > 0
        const computedStyle = window.getComputedStyle(mapContainer)
        const isNotHidden = computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden'

        if (!isVisible || !hasDimensions || !isNotHidden) {
          if (retryCount < maxRetries) {
            console.warn(`Container not ready (attempt ${retryCount + 1}/${maxRetries}), retrying...`)
            setTimeout(() => validateAndInitializeMap(retryCount + 1), retryDelay)
            return
          } else {
            console.error(`Container failed validation after ${maxRetries} attempts`)
            mapContainer.innerHTML = '<div style="padding: 20px; text-align: center; color: #666;">Map container not ready</div>'
            return
          }
        }

        // Force minimum dimensions based on screen size
        const isDesktop = window.innerWidth >= 992
        const minHeight = isDesktop ? '400px' : '250px'

        if (mapContainer.offsetHeight < (isDesktop ? 300 : 200)) {
          console.warn(`Container height too small (${mapContainer.offsetHeight}px), setting minimum height to ${minHeight}`)
          mapContainer.style.height = minHeight
          mapContainer.style.minHeight = minHeight
          mapContainer.style.width = '100%'
        }

        // Proceed with map initialization
        this.initializeLeafletMap(activity, mapId, mapContainer)
      }

      // Start the validation process
      validateAndInitializeMap()
    },

    initializeLeafletMap (activity, mapId, mapContainer) {
      try {
        // Decode polyline
        let coords
        try {
          const decodedCoords = polyline.decode(activity.summary_polyline)

          // Convert from [lat, lng] to [lng, lat] for Leaflet
          coords = decodedCoords.map(([lat, lng]) => [lng, lat])

          // Calculate route distance to verify it's not just a point
          if (coords.length > 1) {
            let totalDistance = 0
            for (let i = 1; i < coords.length; i++) {
              const prev = coords[i - 1]
              const curr = coords[i]
              const distance = Math.sqrt(Math.pow(curr[0] - prev[0], 2) + Math.pow(curr[1] - prev[1], 2))
              totalDistance += distance
            }

            // If route is very short, it might be just start/end points
            if (totalDistance < 0.001) {
              console.warn(`Route appears to be very short (${totalDistance.toFixed(6)} degrees), might be just start/end points`)
              // For very short routes, add a line between start and end points
              if (coords.length === 2) {
              }
            }
          }

          // Validate coordinates are in reasonable ranges
          const validCoords = coords.filter(coord => {
            const lng = coord[0]
            const lat = coord[1]
            return !isNaN(lng) && !isNaN(lat) &&
                   lng >= -180 && lng <= 180 &&
                   lat >= -90 && lat <= 90
          })

          if (validCoords.length !== coords.length) {
            console.warn(`Found ${coords.length - validCoords.length} invalid coordinates`)
          }
        } catch (decodeError) {
          console.error('Error decoding polyline for activity', activity.id, decodeError)
          mapContainer.innerHTML = '<div style="padding: 20px; text-align: center; color: #666;">Invalid route data</div>'
          return
        }

        if (!coords || coords.length === 0) {
          console.warn('No coordinates found for activity', activity.id)
          mapContainer.innerHTML = '<div style="padding: 20px; text-align: center; color: #666;">No route data available</div>'
          return
        }

        // Create Leaflet map with optimized settings
        const map = L.map(mapContainer, {
          zoomControl: true,
          attributionControl: true,
          preferCanvas: true, // Use canvas renderer for better performance
          renderer: L.canvas(), // Explicitly use canvas renderer
          zoomAnimation: true,
          fadeAnimation: true,
          markerZoomAnimation: true
        })

        // Add OpenStreetMap tile layer with optimized settings
        const tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19,
          subdomains: ['a', 'b', 'c'], // Use multiple subdomains for better performance
          tileSize: 256,
          zoomOffset: 0
        })

        tileLayer.addTo(map)

        // Store map reference using unique mapId
        this.activityMaps[mapId] = map

        // Create polyline FIRST to ensure it's rendered properly
        const polylineOptions = {
          color: '#007bff',
          weight: 8,
          opacity: 0.9,
          smoothFactor: 0, // Disable smoothing to preserve all coordinate points
          lineCap: 'round',
          lineJoin: 'round',
          className: 'activity-polyline',
          bubblingMouseEvents: false,
          interactive: true,
          noClip: false // Ensure clipping is disabled
        }

        // Convert coordinates to Leaflet LatLng objects to ensure proper handling
        // coords is already in [lng, lat] format, so we use coord[1] as lat and coord[0] as lng
        const latLngs = coords.map(coord => L.latLng(coord[1], coord[0]))
        const polylineLayer = L.polyline(latLngs, polylineOptions).addTo(map)

        // Force the polyline to be on top of other layers
        polylineLayer.bringToFront()

        // Verify the polyline was created with all points
        // const polylinePoints = polylineLayer.getLatLngs()
        // Verify polyline bounds
        // const polylineBounds = polylineLayer.getBounds()
        // Add hover effect to polyline
        polylineLayer.on('mouseover', function (e) {
          this.setStyle({
            weight: 10,
            opacity: 1
          })
        })

        polylineLayer.on('mouseout', function (e) {
          this.setStyle({
            weight: 8,
            opacity: 0.9
          })
        })

        // Add route markers for start and end points AFTER polyline is created
        if (latLngs.length > 0) {
          const startPoint = latLngs[0]
          const endPoint = latLngs[latLngs.length - 1]

          // Start marker (green circle)
          const startMarker = L.circleMarker(startPoint, {
            radius: 8,
            fillColor: '#28a745',
            color: '#fff',
            weight: 3,
            opacity: 1,
            fillOpacity: 0.9
          }).addTo(map)
          startMarker.bindPopup(`START: ${startPoint.lat.toFixed(6)}, ${startPoint.lng.toFixed(6)}`)

          // End marker (red circle) - only if it's different from start
          if (latLngs.length > 1 && (startPoint.lat !== endPoint.lat || startPoint.lng !== endPoint.lng)) {
            const endMarker = L.circleMarker(endPoint, {
              radius: 8,
              fillColor: '#dc3545',
              color: '#fff',
              weight: 3,
              opacity: 1,
              fillOpacity: 0.9
            }).addTo(map)
            endMarker.bindPopup(`END: ${endPoint.lat.toFixed(6)}, ${endPoint.lng.toFixed(6)}`)
          }

          // Fit map bounds to the polyline with intelligent padding
          const bounds = L.latLngBounds(latLngs)
          // Calculate intelligent padding based on map container size
          const containerWidth = mapContainer.offsetWidth
          const containerHeight = mapContainer.offsetHeight
          const paddingX = Math.min(containerWidth * 0.1, 50)
          const paddingY = Math.min(containerHeight * 0.1, 50)

          map.fitBounds(bounds, {
            padding: [paddingY, paddingX],
            maxZoom: 16 // Prevent excessive zoom for very short routes
          })

          // Ensure map is properly rendered after bounds fitting
          setTimeout(() => {
            map.invalidateSize()
            // Final validation - check if map is actually visible
            if (map.getZoom() < 1 || map.getZoom() > 20) {
              console.warn(`Map zoom level seems invalid: ${map.getZoom()}, resetting...`)
              map.setView(bounds.getCenter(), 13)
            }
          }, 150)
        }

        // Add map loaded class for styling
        mapContainer.classList.add('map-loaded')

        // Force map to recalculate its size after DOM changes
        setTimeout(() => {
          map.invalidateSize()
        }, 50)
      } catch (error) {
        console.error('Error creating Leaflet map for activity', activity.id, error)
        if (mapContainer) {
          mapContainer.innerHTML = '<div style="padding: 20px; text-align: center; color: #666;">Map initialization failed</div>'
        }
      }
    },

    destroyActivityMaps () {
      Object.keys(this.activityMaps).forEach(activityId => {
        if (this.activityMaps[activityId]) {
          this.activityMaps[activityId].remove()
          delete this.activityMaps[activityId]
        }
      })
    },

    handleWindowResize () {
      // Resize all Leaflet maps when window is resized
      Object.keys(this.activityMaps).forEach(mapId => {
        if (this.activityMaps[mapId]) {
          // Leaflet uses invalidateSize() instead of resize()
          this.activityMaps[mapId].invalidateSize()
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.profile-container {
  padding: 2rem 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 100vh;
}

// Profile Header Section
.profile-header-section {
  margin-bottom: 2rem;
}

.profile-header-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.profile-header-content {
  display: flex;
  align-items: center;
  padding: 2rem;
  gap: 2rem;
  position: relative;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(74, 144, 226, 0.02) 0%, rgba(74, 144, 226, 0.05) 100%);
    border-radius: 16px;
    z-index: -1;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
}

.profile-basic-info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.profile-avatar-section {
  position: relative;
}

.profile-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #e9ecef;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
}

.avatar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  cursor: pointer;
}

.profile-avatar-section:hover .avatar-overlay {
  opacity: 1;
}

.change-avatar-text {
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  text-align: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.profile-status-indicator {
  position: absolute;
  bottom: 8px;
  right: 8px;
}

.status-dot {
  color: #2ecc71;
  font-size: 1.2rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.profile-details {
  flex: 1;
}

.profile-name {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 1rem 0;
  line-height: 1.2;
}

.profile-meta {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.profile-info-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #7f8c8d;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &:hover {
    color: #4a90e2;
    transform: translateX(2px);
  }

  &.compact {
    font-size: 0.8rem;
    padding: 0.3rem 0.5rem;
    border-radius: 6px;
    background: rgba(74, 144, 226, 0.05);
    margin: 0.2rem 0;

    &:hover {
      background: rgba(74, 144, 226, 0.1);
      transform: translateX(3px);
    }
  }
}

.meta-icon {
  color: #4a90e2;
  font-size: 0.9rem;
  width: 16px;
  transition: all 0.3s ease;

  &.compact {
    font-size: 0.8rem;
    width: 14px;
  }
}

.meta-label {
  font-weight: 600;
  color: #2c3e50;
  transition: all 0.3s ease;

  &.compact {
    font-size: 0.75rem;
    font-weight: 700;
  }
}

.meta-value {
  color: #7f8c8d;
  font-family: 'Courier New', monospace;
  transition: all 0.3s ease;

  &.compact {
    font-size: 0.75rem;
    font-weight: 600;
    color: #4a90e2;
  }
}

.pro-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-left: 0.5rem;
  padding: 0.2rem 0.6rem;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #333;
  font-size: 0.7rem;
  font-weight: 700;
  font-family: 'Arial', sans-serif;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  animation: shimmer 2s infinite;
  cursor: default;

  .pro-icon {
    font-size: 0.75rem;
    color: #d4af37;
    animation: pulse 2s infinite;
  }

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(255, 215, 0, 0.6);
  }
}

@keyframes shimmer {
  0%, 100% {
    background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  }
  50% {
    background: linear-gradient(135deg, #ffed4e 0%, #ffd700 100%);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.1);
  }
}

.profile-actions {
  flex-shrink: 0;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.btn-edit-profile,
.btn-logout {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  justify-content: center;
  min-width: 140px;
}

.btn-progress-tracker {
  background: linear-gradient(135deg, #FC4C02 0%, #FF6B35 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(252, 76, 2, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(252, 76, 2, 0.4);
  }
}

.btn-edit-profile {
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(74, 144, 226, 0.4);
  }
}

.btn-logout {
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(231, 76, 60, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(231, 76, 60, 0.4);
  }
}

.btn-icon {
  font-size: 0.8rem;
}

// Challenges Section
.challenges-section {
  margin-bottom: 2rem;
}

.challenges-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  gap: 1rem;
}

.section-icon {
  font-size: 2rem;
  color: #ff6b35;
}

.section-title {
  font-size: 2rem;
  font-weight: 700;
  color: #4a90e2;
  margin: 0;
}

// Challenge Cards
.challenge-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  border: 1px solid rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
    border-color: rgba(74, 144, 226, 0.2);
  }
}

.challenge-card-header {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  border-radius: 16px 16px 0 0;
}

.challenge-status-section {
  display: flex;
  gap: 0.5rem;
}

.status-badge {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 600;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
}

.status-badge.upcoming {
  background: linear-gradient(135deg, #00b4d8 0%, #0077b6 100%);
}

.status-badge.expired {
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
}

.status-badge.active {
  background: linear-gradient(135deg, #2ecc71 0%, #27ae60 100%);
}

.status-icon {
  font-size: 0.7rem;
}

.challenge-type-section {
  display: flex;
  gap: 0.5rem;
}

.type-icon {
  font-size: 1rem;
  padding: 0.4rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: scale(1.15);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
}

.type-icon.swim {
  color: #00b4d8;
}

.type-icon.bike {
  color: #06ffa5;
}

.type-icon.run {
  color: #ff6b35;
}

.type-icon.walk {
  color: #7209b7;
}

.challenge-card-body {
  padding: 1rem;
  text-align: center;
}

.challenge-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 0.75rem 0;
  line-height: 1.3;
  letter-spacing: -0.01em;
}

.challenge-timeline {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  background: rgba(74, 144, 226, 0.03);
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(74, 144, 226, 0.1);
}

.timeline-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.timeline-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(74, 144, 226, 0.1);
  color: #4a90e2;
  font-size: 0.7rem;
}

.timeline-content {
  flex: 1;
}

.timeline-label {
  font-size: 0.7rem;
  color: #7f8c8d;
  font-weight: 600;
  margin-bottom: 0.2rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.timeline-value {
  font-size: 0.85rem;
  color: #2c3e50;
  font-weight: 700;
}

.challenge-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.6rem 1rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  justify-content: center;
  flex: 1;
  min-width: 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    text-decoration: none;

    &::before {
      left: 100%;
    }
  }
}

.action-button.primary {
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);

  &:hover {
    box-shadow: 0 6px 20px rgba(74, 144, 226, 0.4);
  }
}

.action-button.secondary {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  color: #2c3e50;
  border: 1px solid #dee2e6;

  &:hover {
    background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
  }
}

.action-button.achievement {
  background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
  color: #2c3e50;
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);

  &:hover {
    box-shadow: 0 6px 20px rgba(255, 215, 0, 0.4);
  }
}

.action-button.ai-gen {
  background: linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(155, 89, 182, 0.3);

  &:hover {
    box-shadow: 0 6px 20px rgba(155, 89, 182, 0.4);
  }
}

.button-icon {
  font-size: 0.7rem;
}

// Mobile Challenge Cards
.challenge-card-mobile {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    border-color: rgba(74, 144, 226, 0.2);
  }
}

.challenge-mobile-header {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0.8rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  border-radius: 12px 12px 0 0;
}

.challenge-mobile-status {
  display: flex;
  gap: 0.3rem;
}

.mobile-status-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
}

.mobile-status-badge.upcoming {
  background: linear-gradient(135deg, #00b4d8 0%, #0077b6 100%);
}

.mobile-status-badge.expired {
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
}

.mobile-status-badge.active {
  background: linear-gradient(135deg, #2ecc71 0%, #27ae60 100%);
}

.mobile-status-icon {
  font-size: 0.7rem;
}

.challenge-mobile-type {
  display: flex;
  gap: 0.3rem;
}

.mobile-type-icon {
  font-size: 0.9rem;
  padding: 0.3rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: scale(1.15);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
}

.mobile-type-icon.swim {
  color: #00b4d8;
}

.mobile-type-icon.bike {
  color: #06ffa5;
}

.mobile-type-icon.run {
  color: #ff6b35;
}

.mobile-type-icon.walk {
  color: #7209b7;
}

.challenge-mobile-content {
  padding: 0.8rem;
  text-align: center;
}

.challenge-mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.6rem;
}

.challenge-mobile-name {
  font-size: 1rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.01em;
}

.mobile-arrow-icon {
  color: #4a90e2;
  font-size: 0.8rem;
  transition: transform 0.3s ease;
}

.challenge-card-mobile:hover .mobile-arrow-icon {
  transform: translateX(4px);
}

.challenge-mobile-dates {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 0.8rem;
  background: rgba(74, 144, 226, 0.03);
  padding: 0.6rem;
  border-radius: 6px;
  border: 1px solid rgba(74, 144, 226, 0.1);
}

.mobile-date-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #7f8c8d;
  font-size: 0.75rem;
}

.mobile-date-icon {
  color: #4a90e2;
  font-size: 0.7rem;
  width: 14px;
}

.mobile-date-text {
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.75rem;
}

.challenge-mobile-actions {
  display: flex;
  gap: 0.4rem;
  justify-content: center;
}

.mobile-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  color: white;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 0.8rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    text-decoration: none;

    &::before {
      left: 100%;
    }
  }
}

.mobile-action-btn.primary {
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);

  &:hover {
    box-shadow: 0 6px 20px rgba(74, 144, 226, 0.4);
  }
}

.mobile-action-btn.secondary {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  color: #2c3e50;
  border: 1px solid #dee2e6;

  &:hover {
    background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
  }
}

.mobile-action-btn.achievement {
  background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
  color: #2c3e50;
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);

  &:hover {
    box-shadow: 0 6px 20px rgba(255, 215, 0, 0.4);
  }
}

.mobile-action-btn.ai-gen {
  background: linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(155, 89, 182, 0.3);

  &:hover {
    box-shadow: 0 6px 20px rgba(155, 89, 182, 0.4);
  }
}

// Empty State
.empty-state {
  padding: 3rem 1rem;
}

.empty-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  margin: 0 auto 1.5rem;
}

.empty-icon {
  font-size: 2rem;
  color: #7f8c8d;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.empty-description {
  color: #7f8c8d;
  font-size: 1rem;
  margin: 0;
}

// Modal Styles
.modal-content {
  padding: 1rem 0;
}

// Edit Profile Modal Alerts
#edit-profile-modal {
  .alert {
    margin-bottom: 1.5rem;
    border-radius: 8px;

    .btn-close {
      padding: 0.5rem;
      background-size: 0.75rem;
    }

    &.alert-danger {
      background-color: #fee;
      border-color: #fcc;
      color: #c33;

      svg {
        color: #c33;
      }
    }

    &.alert-success {
      background-color: #efe;
      border-color: #cfc;
      color: #3c3;

      svg {
        color: #3c3;
      }
    }
  }
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;

  .optional-label {
    margin-left: auto;
    font-weight: 400;
    font-size: 0.85rem;
    color: #6c757d;
    font-style: italic;
  }
}

.form-icon {
  color: #4a90e2;
  font-size: 0.9rem;
}

.form-section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
}

// Sport types multi-select dropdown styling
#target-sport-types {
  min-height: 120px;
  padding: 0.5rem;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: #4a90e2;
    box-shadow: 0 0 0 0.2rem rgba(74, 144, 226, 0.25);
    outline: none;
  }

  option {
    padding: 0.75rem;
    border-radius: 6px;
    margin: 0.25rem 0;
    cursor: pointer;
    font-size: 0.95rem;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #f0f7ff;
    }

    &:checked {
      background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
      color: white;
      font-weight: 500;
    }
  }
}

// Input group styling for weight and height
.input-group {
  display: flex;
  align-items: stretch;

  .form-control {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .input-group-text {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    background-color: #f8f9fa;
    border: 2px solid #e9ecef;
    border-left: 0;
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
    color: #6c757d;
    font-weight: 500;
    font-size: 0.9rem;
  }
}

// Form switch styling for auto share toggle
.form-check {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .form-check-input {
    width: 3rem;
    height: 1.5rem;
    background-color: #e9ecef;
    border: 2px solid #e9ecef;
    border-radius: 1rem;
    transition: all 0.3s ease;

    &:checked {
      background-color: #4a90e2;
      border-color: #4a90e2;
    }

    &:focus {
      box-shadow: 0 0 0 0.2rem rgba(74, 144, 226, 0.25);
    }
  }

  .form-check-label {
    font-weight: 500;
    color: #2c3e50;
    margin-bottom: 0;
    cursor: pointer;
  }
}

.form-text {
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

.form-control {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
  }

  &.is-invalid {
    border-color: #e74c3c;
  }
}

.invalid-feedback {
  color: #e74c3c;
  font-size: 0.85rem;
  margin-top: 0.25rem;
}

// Pagination Styles
.pagination-section {
  margin-top: 2rem;
}

.pagination-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.pagination-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid #4a90e2;
  background: #4a90e2;
  color: white;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 100px;

  &:hover:not(:disabled) {
    background: #357abd;
    border-color: #357abd;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);
  }

  &:disabled {
    background: #e9ecef;
    border-color: #e9ecef;
    color: #adb5bd;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
}

.btn-icon {
  font-size: 0.8rem;
}

.btn-text {
  font-weight: 600;
}

.home-btn {
  background: #28a745;
  border-color: #28a745;

  &:hover:not(:disabled) {
    background: #218838;
    border-color: #218838;
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
  }
}

.pagination-info {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
}

.info-text,
.total-count {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #7f8c8d;
  font-weight: 500;
  font-size: 0.9rem;
}

.info-icon {
  color: #4a90e2;
  font-size: 0.8rem;
}

// Loading Spinner Styles
.loading-spinner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.spinner-icon {
  font-size: 0.9rem;
  color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// Infinite Scroll Loading Indicator
.infinite-scroll-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem 0;
}

.loading-indicator {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: rgba(74, 144, 226, 0.1);
  border-radius: 25px;
  border: 1px solid rgba(74, 144, 226, 0.2);
}

.loading-spinner-icon {
  font-size: 1rem;
  color: #4a90e2;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: #4a90e2;
  font-weight: 500;
  font-size: 0.9rem;
}

  // Responsive Design
  @media (max-width: 768px) {
    .profile-header-content.compact {
      flex-direction: column;
      padding: 1.5rem;
      gap: 1rem;
      background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      margin: 0.5rem;

      &::before {
        background: linear-gradient(135deg, rgba(74, 144, 226, 0.03) 0%, rgba(74, 144, 226, 0.08) 100%);
        border-radius: 12px;
      }

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 6px 25px rgba(0, 0, 0, 0.15);
      }
    }
      .profile-avatar.compact {
      width: 60px;
      height: 60px;
      border: 3px solid #e9ecef;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
      transition: all 0.3s ease;

      &:hover {
        transform: scale(1.1);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
      }
    }
      .profile-name.compact {
      font-size: 1.2rem;
      font-weight: 700;
      color: #2c3e50;
      margin: 0;
      text-align: center;
      line-height: 1.3;
    }
      .action-buttons.compact {
      flex-direction: row;
      justify-content: center;
      gap: 0.75rem;
      width: 100%;
    }
    .btn-progress-tracker.compact,
    .btn-edit-profile.compact,
    .btn-logout.compact {
      min-width: 120px;
      padding: 0.6rem 1rem;
      font-size: 0.85rem;
      border-radius: 10px;
      box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
      }
    }
  .challenges-header {
    flex-direction: column;
    gap: 0.5rem;
  }
  .section-title {
    font-size: 1.5rem;
  }
  .pagination-controls {
    flex-direction: column;
    gap: 1rem;
  }
  .pagination-btn {
    min-width: 80px;
    padding: 0.6rem 1rem;
  }
  .pagination-info {
    flex-direction: column;
    gap: 0.5rem;
  }
}

@media (max-width: 576px) {
  .profile-container {
    padding: 1rem 0;
  }

  .profile-header-content {
    padding: 1rem;
  }

  .profile-header-content.compact {
    padding: 1rem;
    gap: 0.75rem;
    margin: 0.25rem;

    .profile-avatar.compact {
      width: 50px;
      height: 50px;
    }

    .profile-info-item.compact {
      font-size: 0.7rem;
      padding: 0.25rem 0.4rem;
    }

    .meta-icon.compact {
      font-size: 0.7rem;
      width: 12px;
    }

    .meta-label.compact {
      font-size: 0.65rem;
    }

    .meta-value.compact {
      font-size: 0.65rem;
    }

    .btn-edit-profile.compact,
    .btn-logout.compact {
      min-width: 100px;
      padding: 0.5rem 0.8rem;
      font-size: 0.75rem;
    }
  }

  .profile-avatar {
    width: 80px;
    height: 80px;
  }

  .profile-name {
    font-size: 1.25rem;
  }

  .action-buttons {
    flex-direction: column;
    width: 100%;
  }

  .btn-edit-profile,
  .btn-logout {
    width: 100%;
  }
}

.challenge-timeline.single-row {
  flex-direction: row;
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.challenge-timeline .timeline-separator {
  font-size: 1.2rem;
  color: #7f8c8d;
  margin: 0 0.5rem;
  font-weight: 300;
}

.challenge-mobile-dates.single-row {
  flex-direction: row;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.challenge-mobile-dates .timeline-separator {
  font-size: 1rem;
  color: #7f8c8d;
  margin: 0 0.3rem;
  font-weight: 300;
}

// CSS for centered layout
.challenge-timeline.single-row.centered {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 2rem;
}

.challenge-timeline.single-row.centered .timeline-separator {
  font-size: 1.2rem;
  color: #7f8c8d;
  margin: 0 0.5rem;
  font-weight: 300;
}
.challenge-mobile-dates.single-row.centered {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 1.2rem;
}

.challenge-mobile-dates.single-row.centered .timeline-separator {
  font-size: 1rem;
  color: #7f8c8d;
  margin: 0 0.3rem;
  font-weight: 300;
}

// CSS for spread layout
.challenge-timeline.single-row.spread {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 0;
}
.challenge-timeline.single-row.spread .timeline-item {
  flex: 1 1 0;
  text-align: left;
}
.challenge-timeline.single-row.spread .timeline-item.end {
  text-align: right;
}
.challenge-mobile-dates.single-row.spread {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 0;
}
.challenge-mobile-dates.single-row.spread .mobile-date-item {
  flex: 1 1 0;
  text-align: left;
}
.challenge-mobile-dates.single-row.spread .mobile-date-item.end {
  text-align: right;
}

.challenge-mobile-actions.single-row.centered {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 1.2rem;
}

// Achievement Modal Styles
.achievement-modal-body {
  max-height: 80vh;
  overflow-y: auto;
}

.achievement-summary {
  padding: 0.5rem 0;
}

.achievement-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.achievement-title h5 {
  color: #2c3e50;
  font-weight: 700;
  margin: 0;
  text-align: left;
  font-size: 1.1rem;
}

.achievement-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.chart-type-selector {
  display: flex;
  gap: 0.25rem;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0.25rem;
}

.chart-type-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.8rem;

  &:hover {
    background: rgba(74, 144, 226, 0.1);
    color: #4a90e2;
  }

  &.active {
    background: #4a90e2;
    color: white;
    box-shadow: 0 2px 4px rgba(74, 144, 226, 0.3);
  }
}

.btn-stats {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(74, 144, 226, 0.4);
  }
}

.btn-stats .btn-icon {
  font-size: 0.8rem;
}

// Stats Summary Styles
.stats-summary {
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 8px;
  border: 1px solid rgba(74, 144, 226, 0.1);
  min-height: auto;
  overflow: visible;
}

.chart-section {
  margin-bottom: 1rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.4rem;
  margin-top: 2rem;
  position: relative;
  z-index: 1;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 0.4rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  }
}

.stat-header {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.25rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid #e9ecef;
}

.stat-icon {
  font-size: 0.75rem;
}

.stat-icon.swim {
  color: #00b4d8;
}

.stat-icon.bike {
  color: #06ffa5;
}

.stat-icon.run {
  color: #ff6b35;
}

.stat-icon.walk {
  color: #7209b7;
}

.stat-type {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.7rem;
}

.stat-content {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.1rem 0;
}

.stat-label {
  color: #7f8c8d;
  font-size: 0.6rem;
  font-weight: 500;
}

.stat-value {
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.7rem;
}

.activities-section {
  margin-top: 1rem;
}

.activities-header {
  margin-bottom: 0.75rem;
}

.activities-header h6 {
  color: #2c3e50;
  font-weight: 600;
  margin: 0;
}

.activities-table-wrapper {
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.modern-compact-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.modern-compact-table th {
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  color: white;
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.modern-compact-table td {
  padding: 0.75rem;
  border-bottom: 1px solid #e9ecef;
  vertical-align: middle;
}

.modern-compact-table tr:hover {
  background-color: rgba(74, 144, 226, 0.05);
}

.modern-compact-table tr:last-child td {
  border-bottom: none;
}

.activity-icon {
  font-size: 1rem;
  margin-right: 0.5rem;
}

.activity-icon.swim {
  color: #00b4d8;
}

.activity-icon.bike {
  color: #06ffa5;
}

.activity-icon.run {
  color: #ff6b35;
}

.activity-icon.walk {
  color: #7209b7;
}

.activity-icon.brick {
  color: #9b59b6;
}

.activity-name {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// Responsive table
@media (max-width: 768px) {
  .achievement-header {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .achievement-controls {
    justify-content: space-between;
  }

  .modern-compact-table {
    font-size: 0.75rem;
  }

  .modern-compact-table th,
  .modern-compact-table td {
    padding: 0.4rem 0.5rem;
  }

  .activity-icon {
    font-size: 0.8rem;
    margin-right: 0.3rem;
  }

  .activity-name {
    max-width: 120px;
  }

  // Hide moving time column on mobile
  .modern-compact-table th:nth-child(5),
  .modern-compact-table td:nth-child(5) {
    display: none;
  }

  // Stats responsive
  .achievement-title h5 {
    text-align: center;
  }

  .btn-stats {
    justify-content: center;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.3rem;
  }

  .stat-card {
    padding: 0.3rem;
  }

  .stat-header {
    margin-bottom: 0.2rem;
    padding-bottom: 0.2rem;
  }

  .stat-content {
    gap: 0.1rem;
  }

  .stat-item {
    padding: 0.05rem 0;
  }

  .stat-label {
    font-size: 0.55rem;
  }

  .stat-value {
    font-size: 0.65rem;
  }
  .chart-section {
    max-height: 60vh;
    overflow-y: auto;
  }
}

.activities-section.blurred {
  filter: blur(5px);
  pointer-events: none;
  transition: filter 0.3s;
}

// Avatar Upload Modal Styles
.avatar-upload-content {
  padding: 1rem 0;
}

.upload-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.file-upload-area {
  border: 2px dashed #dee2e6;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);

  &:hover {
    border-color: #4a90e2;
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(74, 144, 226, 0.15);
  }

  &.drag-over {
    border-color: #4a90e2;
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    transform: scale(1.02);
    box-shadow: 0 6px 20px rgba(74, 144, 226, 0.25);
  }
}

.upload-icon {
  margin-bottom: 1rem;
}

.upload-icon-svg {
  font-size: 3rem;
  color: #4a90e2;
  transition: all 0.3s ease;
}

.file-upload-area:hover .upload-icon-svg {
  transform: scale(1.1);
  color: #357abd;
}

.upload-text h5 {
  color: #2c3e50;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.upload-text p {
  color: #7f8c8d;
  margin-bottom: 0.5rem;
}

.file-preview {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e9ecef;
}

.preview-header h6 {
  color: #2c3e50;
  font-weight: 600;
  margin: 0;
}

.btn-remove-file {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: #e74c3c;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.8rem;

  &:hover {
    background: #c0392b;
    transform: scale(1.1);
  }
}

.preview-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.preview-image {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e9ecef;
}

.file-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.file-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.file-size {
  color: #7f8c8d;
  font-size: 0.8rem;
}

// Responsive styles for avatar upload
@media (max-width: 768px) {
  .file-upload-area {
    padding: 1.5rem;
  }

  .upload-icon-svg {
    font-size: 2.5rem;
  }

  .upload-text h5 {
    font-size: 1.1rem;
  }

  .preview-content {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
  }

  .preview-image {
    width: 80px;
    height: 80px;
  }
}

// AI Gen Modal Styles
.ai-gen-modal-body {
  max-height: 80vh;
  overflow-y: auto;
}

.ai-gen-summary {
  padding: 0.5rem 0;
}

.ai-gen-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.ai-gen-title h5 {
  color: #2c3e50;
  font-weight: 700;
  margin: 0;
  text-align: left;
  font-size: 1.1rem;
}

.tracklog-section {
  margin-top: 1rem;
}

.tracklog-header {
  margin-bottom: 0.75rem;
}

.tracklog-header h6 {
  color: #2c3e50;
  font-weight: 600;
  margin: 0;
}

.tracklog-table-wrapper {
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.tracklog-name {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  color: #2c3e50;
}

// Table cell containing AI generation buttons
td:has(.btn-ai-generate) {
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
}

.btn-ai-generate {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  background: linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(155, 89, 182, 0.3);
  margin: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(155, 89, 182, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &.image-gen {
    background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
    box-shadow: 0 2px 6px rgba(52, 152, 219, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 15px rgba(52, 152, 219, 0.4);
    }
  }

  &.video-gen {
    background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
    box-shadow: 0 2px 6px rgba(231, 76, 60, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 15px rgba(231, 76, 60, 0.4);
    }
  }
}

.btn-ai-generate .btn-icon {
  font-size: 1rem;
}

// Responsive AI Gen Modal
@media (max-width: 768px) {
  .ai-gen-header {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .ai-gen-title h5 {
    text-align: center;
  }

  .tracklog-name {
    max-width: 120px;
  }

  // Reset table cell flex layout on mobile
  td:has(.btn-ai-generate) {
    display: table-cell;
    white-space: normal;
    padding: 0.25rem;
  }

  .btn-ai-generate {
    width: 3rem;
    height: 3rem;
    margin: 0 0.3rem 0.3rem 0;
    min-width: 3rem;
    min-height: 3rem;
  }

  .btn-ai-generate .btn-icon {
    font-size: 1.1rem;
  }
}

// Mobile Tracklog Cards
.tracklog-mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.tracklog-mobile-card {
  background: white;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
}

.tracklog-mobile-header {
  margin-bottom: 1rem;
}

.tracklog-mobile-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.tracklog-mobile-name {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  line-height: 1.3;
}

.tracklog-mobile-meta {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.tracklog-mobile-type {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #7f8c8d;
  font-weight: 500;
}

.tracklog-mobile-date {
  font-size: 0.8rem;
  color: #7f8c8d;
}

.tracklog-mobile-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
}

.btn-ai-generate-mobile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 0.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-height: 60px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  .btn-icon {
    font-size: 1.2rem;
    margin-bottom: 0.25rem;
  }

  .btn-text {
    font-size: 0.7rem;
    font-weight: 600;
    text-align: center;
  }

  &.image-gen {
    background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
    color: white;

    &:hover {
      box-shadow: 0 4px 12px rgba(52, 152, 219, 0.4);
    }
  }

  &.video-gen {
    background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
    color: white;

    &:hover {
      box-shadow: 0 4px 12px rgba(231, 76, 60, 0.4);
    }
  }

  &.tracklog-stats {
    background: linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%);
    color: white;

    &:hover {
      box-shadow: 0 4px 12px rgba(155, 89, 182, 0.4);
    }

    &::before {
      content: "\f201"; // FontAwesome chart-line icon
      font-family: "Font Awesome 5 Free";
      font-weight: 900;
      margin-right: 0.5rem;
    }
  }
}

// Content Text Styles
.content-text-container {
  max-width: 100%;
  word-wrap: break-word;
}

.content-preview {
  .markdown-content {
    max-height: 100px;
    overflow: hidden;
    line-height: 1.4;
  }
}

// Enhanced content preview styles
.preview-header {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;

  .activity-title {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
    color: #495057;
    line-height: 1.3;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .preview-btn {
    align-self: flex-start;
    font-size: 0.8rem;
    padding: 0.375rem 0.75rem;
    border-radius: 6px;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    }

    &:active {
      transform: translateY(0);
    }
  }
}

// Responsive styles for content preview
@media (max-width: 768px) {
  .preview-header {
    gap: 0.5rem;

    .activity-title {
      font-size: 0.85rem;
      max-width: 200px;
    }

    .preview-btn {
      font-size: 0.75rem;
      padding: 0.3rem 0.6rem;
      width: 100%;
      justify-content: center;
    }
  }
}

@media (max-width: 576px) {
  .preview-header {
    .activity-title {
      max-width: 150px;
      font-size: 0.8rem;
    }
  }
}

.markdown-content {
  line-height: 1.5;

  h1, h2, h3 {
    margin: 0.5rem 0;
    font-weight: 600;
  }

  h1 { font-size: 1.25rem; }
  h2 { font-size: 1.1rem; }
  h3 { font-size: 1rem; }

  strong {
    font-weight: 600;
  }

  em {
    font-style: italic;
  }

  ul, ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }

  li {
    margin: 0.25rem 0;
  }
}

.markdown-content-full {
  line-height: 1.6;
  max-height: 70vh;
  overflow-y: auto;
  padding: 1rem;

  h1, h2, h3 {
    margin: 1rem 0 0.5rem 0;
    font-weight: 600;
    color: #2c3e50;
  }

  h1 { font-size: 1.5rem; }
  h2 { font-size: 1.3rem; }
  h3 { font-size: 1.1rem; }

  p {
    margin: 0.75rem 0;
  }

  strong {
    font-weight: 600;
    color: #34495e;
  }

  em {
    font-style: italic;
  }

  ul, ol {
    margin: 0.75rem 0;
    padding-left: 2rem;
  }

  li {
    margin: 0.5rem 0;
  }
}

.content-text-modal-body {
  padding: 0;
}

// AI Content Modal Styles
.ai-image-modal-body {
  padding: 1.5rem;
}

.ai-content-container {
  text-align: center;
}

.ai-generated-video {
  width: 100%;
  max-width: 800px;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background: #000;
}

.upload-area {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 3rem 2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8f9fa;
}

.upload-area:hover {
  border-color: #007bff;
  background: #e3f2fd;
}

.upload-area:active {
  transform: scale(0.98);
}

.ai-image-header {
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 1rem;
}

.ai-image-wrapper {
  position: relative;
  display: inline-block;
  max-width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background: #f8f9fa;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-generated-image {
  max-width: 100%;
  max-height: 70vh;
  width: auto;
  height: auto;
  border-radius: 8px;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
}

.ai-image-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;

  .btn {
    min-width: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
  }
}

// Loading animation for AI image
.running-animate {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.05);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

// Responsive design for AI image modal
@media (max-width: 768px) {
  .ai-image-modal-body {
    padding: 1rem;
  }

  .ai-image-actions {
    flex-direction: column;

    .btn {
      width: 100%;
      margin-bottom: 0.5rem;
    }
  }

  .ai-generated-image {
    max-height: 50vh;
  }
}

// Main Content Tabs
.main-content-section {
  margin-top: 2rem;
}

.content-tabs {
  margin-bottom: 2.5rem;
  position: relative;
}

.nav-tabs-custom {
  border: none;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 16px;
  padding: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent 0%, rgba(74, 144, 226, 0.3) 50%, transparent 100%);
  }

  .nav-item-custom {
    margin-right: 0;
    flex: 1;

    &:last-child {
      margin-right: 0;
    }

    .nav-link {
      border: none;
      border-radius: 12px;
      padding: 1rem 1.5rem;
      color: #64748b;
      background: transparent;
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
      position: relative;
      font-weight: 500;
      font-size: 0.95rem;
      letter-spacing: 0.025em;
      min-height: 52px;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
        border-radius: 12px;
        opacity: 0;
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        z-index: -1;
        transform: scale(0.95);
      }

      &:hover {
        color: #4a90e2;
        background: rgba(74, 144, 226, 0.08);
        transform: translateY(-1px);
        box-shadow: 0 8px 25px rgba(74, 144, 226, 0.15);

        &::before {
          opacity: 0.05;
          transform: scale(1);
        }
      }

      &.active {
        color: white;
        background: transparent;
        font-weight: 600;
        transform: translateY(-2px);
        box-shadow: 0 12px 35px rgba(74, 144, 226, 0.25);

        &::before {
          opacity: 1;
          transform: scale(1);
        }

        .nav-icon {
          color: white;
          transform: scale(1.1);
        }

        .nav-text {
          color: white;
          font-weight: 600;
        }
      }

      &:active {
        transform: translateY(0);
        transition: transform 0.1s ease;
      }
    }
  }
}

.nav-icon {
  font-size: 1.1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: inherit;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.nav-text {
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: inherit;
}

.tab-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.tab-pane {
  padding: 0;
}

// AI History Styles
/* Content Preview Modal Styles */
.preview-container {
  max-height: 85vh;
  overflow-y: auto;
  text-align: center;
}

.preview-image {
  max-width: 100%;
  max-height: 75vh;
  width: auto;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: transform 0.2s ease;
}

.preview-image:hover {
  transform: scale(1.02);
}

.preview-image.zoomed {
  transform: scale(1.5);
  cursor: zoom-out;
  max-height: none;
  max-width: none;
}

.zoom-hint {
  margin-bottom: 0.5rem;
  text-align: center;
}

.preview-video {
  max-width: 100%;
  max-height: 75vh;
  width: auto;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.preview-loading {
  padding: 3rem;
  color: #6c757d;
}

.preview-error {
  padding: 3rem;
  color: #dc3545;
}

.preview-actions {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1.5rem;
  padding: 1rem;
}

.content-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .preview-container {
    max-height: 90vh;
    padding: 0.5rem;
  }

  .preview-image {
    max-height: 80vh;
    border-radius: 4px;
  }

  .preview-video {
    max-height: 80vh;
    border-radius: 4px;
  }

  .preview-actions {
    flex-direction: column;
    width: 100%;
    gap: 0.5rem;
    padding: 0.5rem;
  }

  .preview-actions .btn {
    width: 100%;
    padding: 0.75rem;
    font-size: 1rem;
  }

  .content-actions {
    flex-direction: column;
    width: 100%;
    gap: 0.5rem;
  }

  .content-actions .btn {
    width: 100%;
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
  }

  /* Full screen modal on mobile */
  .modal-dialog.modal-full {
    margin: 0;
    max-width: 100%;
    height: 100vh;
  }

  .modal-dialog.modal-full .modal-content {
    height: 100vh;
    border-radius: 0;
    border: none;
  }

  .modal-dialog.modal-full .modal-header {
    border-bottom: 1px solid #dee2e6;
    padding: 1rem;
  }

  .modal-dialog.modal-full .modal-body {
    padding: 0.5rem;
    height: calc(100vh - 120px);
    overflow-y: auto;
  }
}

.ai-history-section {
  padding: 2rem;
}

.ai-history-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e9ecef;

  .header-actions {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-left: auto;

    .refresh-btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      transition: all 0.2s ease;

      &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }

    .polling-controls {
      display: flex;
      align-items: center;
      gap: 0.75rem;

      .polling-status {
        font-size: 0.85rem;
        display: flex;
        align-items: center;
        gap: 0.25rem;
        animation: pulse 2s infinite;
      }
    }
  }
}

.ai-history-table-wrapper {
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.content-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;

  &.image {
    background: rgba(74, 144, 226, 0.1);
    color: #4a90e2;
  }

  &.video {
    background: rgba(155, 89, 182, 0.1);
    color: #9b59b6;
  }

  &.tracklog_stats {
    background: rgba(155, 89, 182, 0.1);
    color: #9b59b6;
  }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;

  &.started {
    background: rgba(255, 193, 7, 0.1);
    color: #ffc107;
  }

  &.finished {
    background: rgba(40, 167, 69, 0.1);
    color: #28a745;
  }

  &.failed, &.api_error {
    background: rgba(220, 53, 69, 0.1);
    color: #dc3545;
  }
}

.content-link {
  color: #4a90e2;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    color: #357abd;
    text-decoration: underline;
  }
}

.activity-id {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #495057;
}

.created-at {
  color: #6c757d;
  font-size: 0.9rem;
}

// AI History Cards (Mobile)
.ai-history-cards {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.ai-history-card {
  background: white;
  border: none;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #4a90e2 0%, #357abd 100%);
  }

  .card-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    padding: 1.25rem;
    border-bottom: 1px solid rgba(233, 236, 239, 0.5);
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 0.75rem;
    position: relative;

    .activity-info {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      flex: 1;
      min-width: 0;

      .activity-id {
        font-size: 0.85rem;
        color: #6c757d;
        font-family: 'Courier New', monospace;
        background: rgba(108, 117, 125, 0.1);
        padding: 0.25rem 0.5rem;
        border-radius: 6px;
        display: inline-block;
        width: fit-content;
      }
    }
  }

  .card-body {
    padding: 1.25rem;

    .content-info {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      .content-url {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        flex-wrap: wrap;

        .content-actions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;

          .btn {
            min-height: 40px;
            padding: 0.5rem 1rem;
            border-radius: 10px;
            font-weight: 500;
            font-size: 0.9rem;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            display: flex;
            align-items: center;
            gap: 0.5rem;

            &:hover {
              transform: translateY(-1px);
              box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
            }

            &.btn-outline-primary {
              border-color: #4a90e2;
              color: #4a90e2;

              &:hover {
                background: #4a90e2;
                color: white;
              }
            }

            &.btn-outline-secondary {
              border-color: #6c757d;
              color: #6c757d;

              &:hover {
                background: #6c757d;
                color: white;
              }
            }
          }
        }
      }

      .privacy-controls {
        .privacy-toggle {
          .btn {
            min-height: 40px;
            padding: 0.5rem 1rem;
            border-radius: 10px;
            font-weight: 500;
            font-size: 0.9rem;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            display: flex;
            align-items: center;
            gap: 0.5rem;
            min-width: 100px;

            &:hover {
              transform: translateY(-1px);
              box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
            }

            &.btn-outline-success {
              border-color: #28a745;
              color: #28a745;

              &:hover {
                background: #28a745;
                color: white;
              }
            }

            &.btn-outline-danger {
              border-color: #dc3545;
              color: #dc3545;

              &:hover {
                background: #dc3545;
                color: white;
              }
            }

            &:disabled {
              opacity: 0.6;
              cursor: not-allowed;
              transform: none;
              box-shadow: none;
            }
          }
        }
      }

      .created-at {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #6c757d;
        font-size: 0.85rem;
        padding: 0.5rem 0.75rem;
        background: rgba(108, 117, 125, 0.05);
        border-radius: 8px;
        border-left: 3px solid #4a90e2;

        .time-icon {
          color: #4a90e2;
          font-size: 0.9rem;
        }
      }
    }
  }
}

// Pagination
.pagination-wrapper {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.pagination-info {
  margin-top: 1rem;
  font-size: 0.9rem;
}

// Responsive design
@media (max-width: 768px) {
  .ai-history-section {
    padding: 1rem;
  }

  .ai-history-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;

    .header-actions {
      margin-left: 0;
      width: 100%;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 0.5rem;

      .polling-controls {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
      }
    }
  }

  .nav-tabs-custom {
    border-radius: 12px;
    padding: 6px;
    margin: 0 -0.5rem;

    .nav-item-custom {
      .nav-link {
        padding: 0.75rem 1rem;
        font-size: 0.9rem;
        min-height: 48px;
        border-radius: 10px;
        gap: 0.5rem;

        &:hover {
          transform: translateY(-0.5px);
          box-shadow: 0 6px 20px rgba(74, 144, 226, 0.12);
        }

        &.active {
          transform: translateY(-1px);
          box-shadow: 0 8px 25px rgba(74, 144, 226, 0.2);
        }
      }
    }
  }

  .nav-icon {
    font-size: 1rem;
  }

  .ai-history-cards {
    gap: 1rem;
  }

  .ai-history-card {
    border-radius: 12px;
    margin: 0 -0.5rem;

    &:hover {
      transform: translateY(-1px);
    }

    .card-header {
      padding: 1rem;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;

      .activity-info {
        width: 100%;
        gap: 0.5rem;

        .activity-id {
          font-size: 0.8rem;
          padding: 0.2rem 0.4rem;
        }
      }
    }

    .card-body {
      padding: 1rem;

      .content-info {
        gap: 0.75rem;

        .content-url {
          gap: 0.5rem;

          .content-actions {
            width: 100%;
            gap: 0.5rem;

            .btn {
              min-height: 44px;
              padding: 0.6rem 1rem;
              font-size: 0.85rem;
              flex: 1;
              justify-content: center;
              min-width: 120px;

              &:hover {
                transform: translateY(-0.5px);
              }
            }
          }
        }

        .privacy-controls {
          .privacy-toggle {
            .btn {
              min-height: 44px;
              padding: 0.6rem 1rem;
              font-size: 0.85rem;
              width: 100%;
              justify-content: center;
              min-width: 120px;

              &:hover {
                transform: translateY(-0.5px);
              }
            }
          }
        }

        .created-at {
          font-size: 0.8rem;
          padding: 0.4rem 0.6rem;
          margin-top: 0.5rem;
        }
      }
    }
  }
}

// Privacy Controls
.privacy-column {
  text-align: center;

  .privacy-toggle {
    .btn {
      min-width: 80px;
      font-size: 0.8rem;
      padding: 0.25rem 0.5rem;

      &.btn-outline-success {
        color: #28a745;
        border-color: #28a745;

        &:hover {
          background-color: #28a745;
          color: white;
        }
      }

      &.btn-outline-danger {
        color: #dc3545;
        border-color: #dc3545;

        &:hover {
          background-color: #dc3545;
          color: white;
        }
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }
}

.privacy-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0.5rem 0;

  .privacy-toggle {
    .btn {
      min-width: 80px;
      font-size: 0.8rem;
      padding: 0.25rem 0.5rem;

      &.btn-outline-success {
        color: #28a745;
        border-color: #28a745;

        &:hover {
          background-color: #28a745;
          color: white;
        }
      }

      &.btn-outline-danger {
        color: #dc3545;
        border-color: #dc3545;

        &:hover {
          background-color: #dc3545;
          color: white;
        }
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }
}

// AI Analysis Modal Styles
.ai-analysis-content {
  .current-activity-info {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 1rem;
    border-left: 4px solid #007bff;

    .activity-details {
      p {
        margin-bottom: 0.5rem;
        font-size: 1.1rem;
      }

      .activity-stats {
        .badge {
          font-size: 0.85rem;
          padding: 0.4rem 0.6rem;
        }
      }
    }
  }

  .ai-analysis-text {
    .analysis-content {
      background: #f8f9fa;
      border-radius: 8px;
      padding: 1rem;
      border-left: 4px solid #28a745;

      .analysis-text {
        word-wrap: break-word;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 0.95rem;
        line-height: 1.6;
        margin: 0;
        color: #333;

        h1, h2, h3 {
          color: #2c3e50;
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
        }

        h1 { font-size: 1.5rem; }
        h2 { font-size: 1.3rem; }
        h3 { font-size: 1.1rem; }

        strong {
          color: #2c3e50;
          font-weight: 600;
        }

        em {
          color: #6c757d;
          font-style: italic;
        }

        ul, ol {
          margin: 0.5rem 0;
          padding-left: 1.5rem;
        }

        li {
          margin-bottom: 0.25rem;
        }

        br {
          line-height: 1.8;
        }
      }
    }
  }

  // AI Loading Animation
  .ai-loading-animation {
    .loading-spinner {
      margin: 1rem 0;
    }

    .loading-dots {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 1rem;

      span {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #007bff;
        margin: 0 4px;
        animation: loading-dots 1.4s infinite ease-in-out both;

        &:nth-child(1) {
          animation-delay: -0.32s;
        }

        &:nth-child(2) {
          animation-delay: -0.16s;
        }

        &:nth-child(3) {
          animation-delay: 0s;
        }
      }
    }
  }

  @keyframes loading-dots {
    0%, 80%, 100% {
      transform: scale(0.8);
      opacity: 0.5;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }

  .analysis-stats {
    .stat-card {
      background: #fff;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      padding: 1rem;
      text-align: center;

      .stat-label {
        font-size: 0.85rem;
        color: #6c757d;
        margin-bottom: 0.5rem;
        text-transform: uppercase;
        font-weight: 600;
      }

      .stat-value {
        font-size: 1.2rem;
        font-weight: bold;
        color: #495057;
      }
    }
  }
}

.running-animate {
  animation: run-bounce 1s infinite linear;
}

@keyframes run-bounce {
  0% { transform: translateY(0); }
  20% { transform: translateY(-8px); }
  40% { transform: translateY(0); }
  100% { transform: translateY(0); }
}

// Activities Styles
.activities-section {
  padding: 1.5rem 0;

  .activities-header {
    display: flex;
    align-items: center;
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid #e9ecef;

    .section-icon {
      margin-right: 1rem;
      font-size: 1.5rem;
    }

    .section-title {
      margin: 0;
      font-size: 1.8rem;
      font-weight: 600;
      color: #495057;
    }
  }

  .activities-grid {
    .activity-card {
      background: white;
      border-radius: 12px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
      overflow: hidden;
      transition: all 0.3s ease;
      height: 100%;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
      }

      .activity-card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 1.5rem;
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        border-bottom: 1px solid #dee2e6;

        .activity-type-badge {
          display: flex;
          align-items: center;
          gap: 0.5rem;

          .activity-icon {
            font-size: 1.2rem;
            color: #007bff;
          }

          .activity-type-text {
            font-weight: 600;
            color: #495057;
            font-size: 0.9rem;
          }
        }

        .activity-header-actions {
          display: flex;
          align-items: center;
          gap: 1rem;

          .activity-date {
            font-size: 0.85rem;
            color: #6c757d;
            font-weight: 500;
          }
        }
      }

      // AI Analysis Button
      .btn-ai-analysis-action {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        border: none;
        border-radius: 20px;
        font-weight: 600;
        font-size: 0.875rem;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        position: relative;
        overflow: hidden;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
          transition: left 0.5s;
        }

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
          background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);

          &::before {
            left: 100%;
          }
        }

        &:active {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
        }

        .btn-icon-ai {
          font-size: 1rem;
          animation: pulse-brain 2s ease-in-out infinite;
        }

        .btn-text-ai {
          font-weight: 600;
          letter-spacing: 0.3px;
        }
      }

      @keyframes pulse-brain {
        0%, 100% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.1);
        }
      }

      // Responsive AI Analysis Button
      @media (max-width: 768px) {
        .btn-ai-analysis-action {
          padding: 0.4rem 0.75rem;
          font-size: 0.75rem;
          border-radius: 16px;

          .btn-icon-ai {
            font-size: 0.875rem;
          }

          .btn-text-ai {
            display: none; // Hide text on very small screens
          }
        }
      }

      @media (min-width: 769px) and (max-width: 991px) {
        .btn-ai-analysis-action {
          padding: 0.45rem 0.85rem;
          font-size: 0.8rem;
        }
      }

      .activity-card-body {
        padding: 1.5rem;

        // Enhanced padding for desktop
        @media (min-width: 992px) {
          padding: 2rem;
        }

        .activity-name {
          font-size: 1.1rem;
          font-weight: 600;
          color: #212529;
          margin-bottom: 0.5rem;
          line-height: 1.4;

          // Enhanced typography for desktop
          @media (min-width: 992px) {
            font-size: 1.25rem;
            margin-bottom: 0.75rem;
          }
        }

        .activity-event {
          font-size: 0.9rem;
          color: #6c757d;
          margin-bottom: 1.5rem;
          font-style: italic;
        }

        .activity-metrics {
          margin-bottom: 1.5rem;

          .metric-row {
            display: flex;
            gap: 1rem;
            margin-bottom: 0.75rem;

            &:last-child {
              margin-bottom: 0;
            }

            // Enhanced layout for desktop
            @media (min-width: 992px) {
              gap: 1.5rem;
              margin-bottom: 1rem;
            }

            .metric-item {
              flex: 1;
              display: flex;
              align-items: center;
              gap: 0.5rem;
              padding: 0.75rem;
              background: #f8f9fa;
              border-radius: 10px;
              border-left: 4px solid #007bff;
              transition: all 0.2s ease;

              // Enhanced styling for desktop
              @media (min-width: 992px) {
                padding: 1rem;
                border-radius: 12px;

                &:hover {
                  background: #e9ecef;
                  transform: translateY(-1px);
                  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
                }
              }

              .metric-icon {
                font-size: 0.9rem;
                color: #007bff;
                min-width: 16px;

                @media (min-width: 992px) {
                  font-size: 1rem;
                  min-width: 18px;
                }
              }

              .metric-label {
                font-size: 0.8rem;
                color: #6c757d;
                font-weight: 500;
                flex-shrink: 0;

                @media (min-width: 992px) {
                  font-size: 0.85rem;
                }
              }

              .metric-value {
                font-size: 0.85rem;
                font-weight: 600;
                color: #495057;
                margin-left: auto;

                @media (min-width: 992px) {
                  font-size: 0.9rem;
                  font-weight: 700;
                }
              }
            }
          }
        }

        .activity-map-container {
          margin-top: 1rem;

          .activity-map {
            width: 100%;
            height: 250px;
            border-radius: 8px;
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            position: relative;
            overflow: hidden;
            min-height: 250px;

            // Enhanced height for desktop view
            @media (min-width: 992px) {
              height: 400px;
              min-height: 400px;
            }

            &::before {
              content: 'Loading map...';
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              z-index: 1;
              color: #6c757d;
              font-size: 0.9rem;
              background: rgba(248, 249, 250, 0.9);
              padding: 10px 20px;
              border-radius: 4px;
              border: 1px solid #dee2e6;
            }

            &.map-loaded::before {
              display: none;
            }

            // Ensure Leaflet container takes full space
            .leaflet-container {
              width: 100% !important;
              height: 100% !important;
              border-radius: 8px;
              z-index: 1;
            }

            // Custom polyline styling
            .activity-polyline {
              filter: drop-shadow(0 2px 4px rgba(0, 123, 255, 0.3));
              stroke: #007bff !important;
              stroke-width: 8px !important;
              stroke-opacity: 0.9 !important;
              fill: none !important;
            }

            // Ensure polyline is always visible
            .leaflet-interactive {
              stroke: #007bff !important;
              stroke-width: 8px !important;
            }

            // Ensure map tiles load properly
            .leaflet-tile-container {
              will-change: transform;
            }

            // Improve map rendering performance
            .leaflet-map-pane {
              will-change: transform;
            }

            // Style Leaflet controls
            .leaflet-control-zoom {
              border: none !important;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
              border-radius: 6px !important;
            }

            .leaflet-control-zoom a {
              background-color: #fff !important;
              color: #333 !important;
              font-weight: bold !important;
            }

            .leaflet-control-zoom a:hover {
              background-color: #f8f9fa !important;
            }

            .leaflet-control-attribution {
              background-color: rgba(255, 255, 255, 0.9) !important;
              border-radius: 4px !important;
              font-size: 10px !important;
              padding: 2px 4px !important;
            }

            // Ensure polyline routes are visible
            .leaflet-interactive {
              pointer-events: auto !important;
            }

            .leaflet-polyline {
              stroke: #007bff !important;
              stroke-width: 8px !important;
              stroke-opacity: 1 !important;
              z-index: 1000 !important;
            }

            .custom-marker {
              z-index: 2000 !important;
            }

            // Style for error states
            > div[style*="padding: 20px"] {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              background: rgba(248, 249, 250, 0.9);
              border: 1px solid #dee2e6;
              border-radius: 4px;
            }
          }
        }
      }
    }
  }

  .empty-state {
    .empty-icon {
      font-size: 4rem;
      color: #dee2e6;
      margin-bottom: 1rem;
    }

    .empty-title {
      color: #6c757d;
      margin-bottom: 0.5rem;
    }

    .empty-description {
      color: #adb5bd;
      font-size: 0.95rem;
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .dashboard-section {
    .activities-grid {
      .activity-card {
        .activity-card-body {
          .activity-metrics {
            .metric-row {
              flex-direction: column;
              gap: 0.5rem;

              .metric-item {
                .metric-value {
                  margin-left: 0;
                }
              }
            }
          }
        }
      }
    }
  }
}

// No Activities Modal Styles
.too-few-activities-modal-content {
  padding: 1.5rem 0;

  .modal-icon {
    margin-bottom: 1.5rem;

    .fa-3x {
      color: #6c757d;
      opacity: 0.8;
    }
  }

  .modal-message {
    .lead {
      font-size: 1.1rem;
      font-weight: 500;
      color: #495057;
      margin-bottom: 1.5rem;
    }

    .modal-features {
      background: #f8f9fa;
      border-radius: 12px;
      padding: 1.5rem;
      margin-bottom: 1.5rem;

      .feature-item {
        display: flex;
        align-items: center;
        font-size: 0.95rem;
        color: #6c757d;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  .modal-actions {
    .btn {
      border-radius: 10px;
      font-weight: 600;
      transition: all 0.3s ease;

      &.btn-primary {
        background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
        border: none;
        box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 123, 255, 0.4);
        }
      }

      &.btn-outline-primary {
        border: 2px solid #007bff;
        color: #007bff;

        &:hover {
          background: #007bff;
          border-color: #007bff;
          transform: translateY(-1px);
        }
      }
    }
  }

  // Mobile responsive adjustments
  @media (max-width: 768px) {
    padding: 1rem 0;

    .modal-message {
      .lead {
        font-size: 1rem;
      }

      .modal-features {
        padding: 1rem;

        .feature-item {
          font-size: 0.9rem;
        }
      }
    }

    .modal-actions {
      .btn {
        font-size: 0.95rem;
        padding: 0.75rem 1rem;
      }
    }
  }
}
</style>
